import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import {
  Plus,
  Bell,
  MoreVertical,
  LogIn,
  Calendar as CalendarIcon,
  RefreshCw,
  ChevronDown,
  CalendarCheck,
  MessageCircle,
  AlertCircle,
  X,
  Trash2,
  User,
  Hash,
  Clock,
  Video,
  Moon,
  Sun,
  CheckCircle,
  XCircle,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Users,
  Eye,
  EyeOff,
  Copy,
  Lock
} from "lucide-react";
import DateTimeHeader from "../components/DateTimeHeader";
import { useNavigate } from "react-router-dom";
import CreateMeeting from "./CreateMeeting";
import Dropdown from "../components/Dropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-custom.css";
import NavBar from "../components/NavBar";
import { useTheme } from "../contexts/ThemeContext";
import { useMeeting } from "../contexts/MeetingContext";
import { useAuth } from "../contexts/AuthContext";
import { v4 as uuidv4 } from 'uuid';
import {
  getParticipantProfile,
  getParticipantAvatar,
  getParticipantDisplayName,
  getParticipantEmail,
  hasRealProfile,
} from '../utils/participantProfileHelper';
import { usePreference } from "../contexts/PreferenceContext";
import { flushLocalMessages, generateToken } from "../utils/firebase";

const dateFilterOptions = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "7days" },
  { label: "Last 2 weeks", value: "14days" },
  { label: "Last month", value: "1month" },
  { label: "Last year", value: "1year" },
];

// Company-wide broadcast announcements
const broadcastAnnouncements = [
  {
    id: 1,
    title: "Office Holiday Party",
    message:
      "Join us for our annual holiday celebration on December 22nd at 6 PM in the main conference hall. Food, drinks, and prizes await!",
    type: "event",
    priority: "medium",
    timestamp: "2024-12-15T10:00:00Z",
    author: "HR Department",
    expiresAt: "2025-01-15T18:00:00Z",
  },
  {
    id: 2,
    title: "System Maintenance Notice",
    message:
      "Our video conferencing system will undergo maintenance on December 20th from 2 AM to 4 AM EST. Plan accordingly for any scheduled meetings.",
    type: "maintenance",
    priority: "high",
    timestamp: "2024-12-14T09:00:00Z",
    author: "IT Department",
    expiresAt: "2025-01-20T08:00:00Z",
  },
  {
    id: 3,
    title: "New Feature: Waiting Room",
    message:
      "We've launched a new waiting room feature for enhanced meeting security. Hosts can now control who enters their meetings. Learn more in our help center.",
    type: "feature",
    priority: "low",
    timestamp: "2024-12-13T14:30:00Z",
    author: "Product Team",
    expiresAt: "2025-02-28T23:59:59Z",
  },
  {
    id: 4,
    title: "Q4 All-Hands Meeting",
    message:
      "Don't miss our quarterly all-hands meeting this Friday at 3 PM EST. We'll be reviewing achievements and setting goals for Q1.",
    type: "meeting",
    priority: "high",
    timestamp: "2024-12-12T11:00:00Z",
    author: "Executive Team",
    expiresAt: "2025-01-30T15:00:00Z",
  },
];

const Dashboard: React.FC = () => {
  // Helper function to convert 24-hour time to 12-hour format with AM/PM
  const formatTimeToAMPM = (timeString: string) => {
    if (!timeString) return "9:00 AM - 10:00 AM";

    const times = timeString.split("-");
    if (times.length !== 2) return timeString;

    const formatSingleTime = (time: string) => {
      const [hours, minutes] = time.split(":").map(Number);
      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    };

    return `${formatSingleTime(times[0])} - ${formatSingleTime(times[1])}`;
  };

  const [time, setTime] = useState<string>("");
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [showJoinMeeting, setShowJoinMeeting] = useState(false);
  const [showScheduleMeeting, setShowScheduleMeeting] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [selectedDateFilter, setSelectedDateFilter] = useState("today");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const navigate = useNavigate();
  const [joinInput, setJoinInput] = useState("");
  const [openMeetings, setOpenMeetings] = useState<any[]>([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  // const [meetingsError, setMeetingsError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [clearedNotifications, setClearedNotifications] = useState<any[]>([]);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationsTab, setNotificationsTab] = useState<
    "current" | "cleared"
  >("current");
  const [collapsed, setCollapsed] = useState(false);
  const [showMeetingDetails, setShowMeetingDetails] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
  console.log("selectedMeeting1111", selectedMeeting);

  const [recurringEndDate, setRecurringEndDate] = useState<Date | null>(null);
  const [recurringStartDate, setRecurringStartDate] = useState<Date | null>(new Date());
  const [recurringFrequency, setRecurringFrequency] = useState<"None" | "Daily" | "Weekly" | "Biweekly" | "Monthly" | "Yearly">("None");
  const [recurringDays, setRecurringDays] = useState<string[]>(["Monday"]);

  const [participantInput, setParticipantInput] = useState("");
  const [showParticipantSuggestions, setShowParticipantSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [realUsers, setRealUsers] = useState<Array<{ id: number, name: string, email: string, avatar: string | null }>>([]);
  const [pendingNewParticipants, setPendingNewParticipants] = useState<string[]>([]);
  const [participantDuplicateMsg, setParticipantDuplicateMsg] = useState<string | null>(null);
  const { formatTime, formatDate } = usePreference();


  // console.log("selectedMeeting", selectedMeeting);

  const [meetingsTab, setMeetingsTab] = useState<
    "all" | "created" | "completed" | "cancelled"
  >("all");
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    time: "",
    recurring: "",
    participants: [] as string[],
    host_name: "",
    room: "",
    status: "",
    date: new Date(),
    startTime: "10:00 AM",
    endTime: "11:00 AM",
    recurringStartDate: null,
    recurringEndDate: null,
    recurringFrequency: "None",
    repeatDays: [] as string[],

  });
  const { darkMode } = useTheme();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCancelMeetingModal, setShowCancelMeetingModal] = useState(false);
  const [showMeetingOptionsDropdown, setShowMeetingOptionsDropdown] =
    useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteList, setInviteList] = useState<string[]>([]);
  // const [showParticipantSuggestions, setShowParticipantSuggestions] =
  //   useState(false);
  // const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);

  const [meetings, setMeetings] = useState([
    {
      title: "Team Sync",
      date: "Today",
      time: "09:00-10:00",
      createdByMe: false,
      status: "upcoming",
      avatars: [
        "https://randomuser.me/api/portraits/men/32.jpg",
        "https://randomuser.me/api/portraits/women/44.jpg",
        "https://randomuser.me/api/portraits/men/45.jpg",
      ],
    },
  ]);

  // Mock user data for autocomplete
  const mockUsers = [
    {
      id: 1,
      name: "John Smith",
      email: "john.smith@company.com",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      id: 2,
      name: "Sarah Johnson",
      email: "sarah.johnson@company.com",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg",
    },
    {
      id: 3,
      name: "Mike Davis",
      email: "mike.davis@company.com",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
    },
    {
      id: 4,
      name: "Emily Wilson",
      email: "emily.wilson@company.com",
      avatar: "https://randomuser.me/api/portraits/women/46.jpg",
    },
    {
      id: 5,
      name: "David Brown",
      email: "david.brown@company.com",
      avatar: "https://randomuser.me/api/portraits/men/47.jpg",
    },
    {
      id: 6,
      name: "Lisa Anderson",
      email: "lisa.anderson@company.com",
      avatar: "https://randomuser.me/api/portraits/women/48.jpg",
    },
    {
      id: 7,
      name: "Alex Thompson",
      email: "alex.thompson@company.com",
      avatar: "https://randomuser.me/api/portraits/men/49.jpg",
    },
    {
      id: 8,
      name: "Maria Garcia",
      email: "maria.garcia@company.com",
      avatar: "https://randomuser.me/api/portraits/women/50.jpg",
    },
  ];
  const [showStatsModal, setShowStatsModal] = useState(false);
  const { user, token, searchUsers } = useAuth();
  // console.log("use111r", user);

  const [selectedStatType, setSelectedStatType] = useState<
    "scheduled" | "attended" | "cancelled"
  >("scheduled");
  const [cameFromStatsModal, setCameFromStatsModal] = useState(false);
  const [createMeetingForm, setCreateMeetingForm] = useState({
    title: "",
    description: "",
    participants: [],
  });
  const [link, setLink] = useState("");
  const [scheduleModalTab, setScheduleModalTab] = useState<
    "details" | "security"
  >("details");

  const [showLoginError, setShowLoginError] = useState('');


  // Function to generate random password
  const generateRandomPassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };


  const {
    meetings: contextMeetings,
    loading: meetingsLoading,
    error: meetingsError,
    createMeeting,
    getUserMeetings,
    getParticipantMeetings,
    cancelMeeting,
    editMeeting,
    sendInvites,
    joinMeeting,
    getMeetingDetails,
    // syncMeetingToCalendars,
    fetchAllMeetings,
    jitsiToken,
    getMeetingById,
  } = useMeeting();

  // console.log("meeting data----",contextMeetings);

  const handleGetDetails = async (meetingId: number) => {
    try {
      const details = await getMeetingDetails(meetingId);
      console.log(details.meeting);
      console.log(details.userRole.canJoin); // Check if user can join
    } catch (error) {
      console.error('Failed to get meeting details:', error);
    }
  };

  const handleDayToggle = (day: string) => {
    setRecurringDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };


  const [dynamicBroadcastAnnouncements, setDynamicBroadcastAnnouncements] = React.useState<any[]>([]);
  console.log("dynamicBroadcastAnnouncements", dynamicBroadcastAnnouncements);

  React.useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://vela-back.wingtrill.com';
        const res = await fetch(`${backendUrl}/api/admin/general-settings`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const data = await res.json();
        console.log("announcement data", data);

        const s = data?.settings;
        if (s?.announcement_message) {
          setDynamicBroadcastAnnouncements([
            {
              id: 999,
              title: s.announcement_title || 'Announcement',
              message: s.announcement_message,
              type: s.announcement_severity || 'info',
              priority: s.announcement_severity || 'info',
              timestamp: new Date().toISOString(),
              author: 'Admin',
              expiresAt: s.announcement_expires_at || null,
            }
          ]);
        } else {
          setDynamicBroadcastAnnouncements([]);
        }
      } catch { }
    };
    fetchAnnouncements();
  }, []);


  const handleParticipantInputChange = async (value: string) => {
    setParticipantInput(value);

    if (value.trim()) {
      const participants = selectedMeeting?.participants || [];

      // Filter local users
      const filteredLocal = realUsers.filter(
        (user) =>
          !participants.includes(user.email) &&
          (user.name?.toLowerCase().includes(value.toLowerCase()) ||
            user.email?.toLowerCase().includes(value.toLowerCase()))
      );

      if (token && value.length >= 2) {
        setIsLoadingUsers(true);
        try {
          const searchResults = await searchUsers(value);
          console.log("searchResults", searchResults, value);
          const filteredResults = searchResults.filter(
            (user) => !participants.includes(user.email)
          );
          setRealUsers(searchResults);
        } catch (error) {
          console.error("Error searching users:", error);
        } finally {
          setIsLoadingUsers(false);
        }
      }

      setFilteredSuggestions(filteredLocal);
      setShowParticipantSuggestions(true);
    } else {
      setShowParticipantSuggestions(false);
      setFilteredSuggestions([]);
    }
  };

  const handleAddParticipant = (email: string) => {
    const normalized = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) return;

    const existingEmails: string[] = (selectedMeeting?.participants || []).map(
      (e: string) => e.toLowerCase()
    );
    if (existingEmails.includes(normalized)) {
      setParticipantDuplicateMsg(`${normalized} is already in the list`);
      setTimeout(() => setParticipantDuplicateMsg(null), 3000);
      setParticipantInput("");
      setShowParticipantSuggestions(false);
      setFilteredSuggestions([]);
      return;
    }

    // Stage locally — invite sent on Save Changes
    setSelectedMeeting({
      ...selectedMeeting,
      participants: [...(selectedMeeting?.participants || []), normalized],
    });
    setPendingNewParticipants((prev) => [...prev, normalized]);
    setParticipantDuplicateMsg(null);

    setParticipantInput("");
    setShowParticipantSuggestions(false);
    setFilteredSuggestions([]);
  };


  const handleRemoveParticipant = async (email: string) => {
    try {
      // Add API call to remove participant if needed
      setSelectedMeeting({
        ...selectedMeeting,
        participants: selectedMeeting.participants.filter((p: any) => p !== email)
      });
    } catch (error) {
      console.error('Failed to remove participant:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && participantInput.trim()) {
      e.preventDefault();
      if (filteredSuggestions.length > 0) {
        handleAddParticipant(filteredSuggestions[0].email);
      } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participantInput.trim())) {
        handleAddParticipant(participantInput.trim());
      }
    }
  };


  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [enteredPassword, setEnteredPassword] = useState("");
  const [currentMeetingId, setCurrentMeetingId] = useState<number | null>(null);
  const [passwordError, setPasswordError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [lockout, setLockout] = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(30);


  const MAX_ATTEMPTS = 5;

  const openPasswordModal = (meetingId: number) => {
    setCurrentMeetingId(meetingId);
    setEnteredPassword("");
    setPasswordError("");
    setAttempts(0);
    setPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordError("");
    setAttempts(0);
  };

  const joinMeetingNew = async (meetingId: number, password?: string) => {
    try {
      const result = await joinMeeting(meetingId, password);

      console.log('Jitsi token:', result.token);

      const meetingUrl = result.meeting.meeting_link;
      window.open(meetingUrl, "_blank");

    } catch (error: any) {
      console.error("Failed to join meeting:", error);

      // Show modal instead of prompt
      if (error.message.includes("password")) {
        openPasswordModal(meetingId);
      } else {
        alert("Unable to join meeting. Please try again.");
      }
    }
  };


  const handleSubmitPassword = async () => {
    if (!currentMeetingId) return;

    try {
      const result = await joinMeeting(currentMeetingId, enteredPassword);

      const meetingUrl = result.meeting.meeting_link;
      window.open(meetingUrl, "_blank");

      closePasswordModal();  // success → close modal

    } catch (error: any) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= MAX_ATTEMPTS) {
        setPasswordError("Too many attempts! Try again in 30 seconds.");
        setLockout(true);

        // Start 30-second countdown
        let timeLeft = 30;
        setLockoutTimer(timeLeft);

        const interval = setInterval(() => {
          timeLeft -= 1;
          setLockoutTimer(timeLeft);

          if (timeLeft <= 0) {
            clearInterval(interval);
            setLockout(false);
            setAttempts(0);
            setPasswordError("");
          }
        }, 1000);

        return;
      }

      setPasswordError("Incorrect password. Try again.");
    }
  };


  function getTodayMeetingSummary(contextMeetings: any[]) {
    if (!contextMeetings || contextMeetings.length === 0)
      return { todayMeetings: [], nextMeeting: null, timeLeft: "" };

    const now = new Date();
    const today = now.toISOString().split("T")[0];

    // ❌ Skip cancelled meetings
    const validMeetings = contextMeetings.filter(m => !m.canceled_meeting);

    // 1️⃣ Filter today's meetings
    const todayMeetings = validMeetings.filter((m) => {
      if (!m.meeting_date) return false;
      const meetingDate = new Date(m.meeting_date).toISOString().split("T")[0];
      return meetingDate === today;
    });

    if (todayMeetings.length === 0)
      return { todayMeetings: [], nextMeeting: null, timeLeft: "" };

    // 2️⃣ Parse start times and find next upcoming one
    const nowTime = now.getTime();

    const upcoming = todayMeetings
      .map((m) => {
        const meetingDateTime = new Date(`${m.meeting_date.split("T")[0]}T${m.start_time}`);
        return { ...m, startDateTime: meetingDateTime };
      })
      .filter((m) => m.startDateTime.getTime() > nowTime)
      .sort((a, b) => a.startDateTime - b.startDateTime);

    const nextMeeting = upcoming.length > 0 ? upcoming[0] : null;

    // 3️⃣ Calculate time left
    let timeLeft = "";
    if (nextMeeting) {
      const diffMs = nextMeeting.startDateTime - nowTime;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;

      if (diffMinutes <= 0) timeLeft = "Starting now!";
      else if (hours > 0) timeLeft = `${hours}h ${minutes}m left until your next meeting`;
      else timeLeft = `${minutes} minutes left until your next meeting`;
    }

    return { todayMeetings, nextMeeting, timeLeft };
  }


  const { todayMeetings, nextMeeting, timeLeft } = getTodayMeetingSummary(contextMeetings);

  useEffect(() => {
    const interval = setInterval(() => {
      setSummary(getTodayMeetingSummary(meetings));
    }, 60000); // every 1 min
    return () => clearInterval(interval);
  }, [meetings]);
  const [summary, setSummary] = useState(getTodayMeetingSummary(contextMeetings));


  useEffect(() => {
    if (user && token) {
      fetchAllMeetings();
    }
  }, [user, token]);



  const [meetingId, setMeetingId] = useState<string>("");

  const generateMeetingLink = (title: string) => {
    const baseUrl = process.env.REACT_APP_MEETING_BASE_URL || "https://dev-meeting.vela.business/";
    const meetingId = uuidv4();



    // Replace letters (a-f) with numbers deterministically (a→1, b→2, etc.)
    const numeric = meetingId
      .toLowerCase()
      .replace(/[a-f]/g, (char) => (char.charCodeAt(0) - 86).toString());

    // Create a URL-friendly slug from the title
    const titleSlug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric chars with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens

    // Combine title slug with UUID (take first 8 chars of UUID for brevity)
    // const shortId = meetingId.substring(0, 8);
    const shortId = numeric.substring(0, 4);
    setMeetingId(shortId);
    const meetingUrl = titleSlug
      ? `${baseUrl}${shortId}-${titleSlug}`
      : `${baseUrl}${meetingId}`;

    return meetingUrl;
  };


  const handleCreateMeeting = async () => {
    if (!token) {
      setShowLoginError("You must be logged in to create a meeting.");
      return;
    }

    try {
      const meetingData = {
        title: createMeetingForm.title || "Quick Meeting",
        description: createMeetingForm.description || "Instant video call",
        meetingType: "instant" as const,
        passwordProtection: securitySettings.requirePassword,
        meetingPassword: securitySettings.password,
        waitingRoom: securitySettings.waitingRoom,
        muteOnEntry: securitySettings.muteOnEntry,
        allowRecording: securitySettings.recording,
        participants: createMeetingForm.participants,
        meeting_link: link,
        meetingId: meetingId,
      };

      // Create the meeting and get the created meeting object
      const createdMeeting = await createMeeting(meetingData);
      console.log("Created meeting:", createdMeeting);



      // Join the meeting to get JWT token
      const result = await joinMeeting(createdMeeting?.id, securitySettings.password);
      fetchAllMeetings();

      // Navigate to meeting with JWT token
      const meetingUrl = result.meeting.meeting_link;
      window.open(meetingUrl, "_blank");

      // Clean up form
      setShowCreateMeeting(false);
      setCreateMeetingForm({ title: "", description: "", participants: [] });

    } catch (err) {
      console.error("Failed to create/join meeting:", err);
      alert("Failed to create meeting. Please try again.");
    }
  };
  // Handle Cancel Meeting
  const handleCancelMeeting = async () => {
    if (selectedMeeting) {
      try {
        await cancelMeeting(selectedMeeting.id);
        setShowCancelMeetingModal(false);
        setShowMeetingDetails(false);
      } catch (err) {
        alert("Failed to cancel meeting. Please try again.");
      }
    }
  };

  // Handle Edit Meeting
  const handleSaveEdit = async () => {
    try {
      // Map recurring string to allowed repeatStatus values
      const recurringMap: { [key: string]: "daily" | "weekly" | "bi-weekly" | "monthly" | "yearly" | undefined } = {
        daily: "daily",
        weekly: "weekly",
        "bi-weekly": "bi-weekly",
        monthly: "monthly",
        yearly: "yearly",
        none: undefined,
      };
      const repeatStatusValue = recurringMap[recurringFrequency] ?? undefined;

      const updates = {
        title: editForm.title,
        description: editForm.description,
        meetingDate: editForm.date,
        startTime: editForm.startTime,
        endTime: editForm.endTime,
        isRecurring: recurringFrequency !== 'None' ? true : false,
        repeatStatus: repeatStatusValue,
        repeatDays: editForm.endTime && recurringFrequency !== 'Daily' ? recurringDays : [],
        recurringStartDate: editForm.endTime ? (recurringStartDate ?? undefined) : undefined,
        recurringEndDate: editForm.endTime ? (recurringEndDate ?? undefined) : undefined,
        recurringFrequency: recurringFrequency,
        // Add security settings
        passwordProtection: securitySettings.requirePassword,
        meetingPassword: securitySettings.password,
        waitingRoom: securitySettings.waitingRoom,
        muteOnEntry: securitySettings.muteOnEntry,
        allowRecording: securitySettings.recording,
      };

      console.log("Updates to save:", updates);

      await editMeeting(selectedMeeting.id, updates);

      if (pendingNewParticipants.length > 0) {
        await sendInvites(selectedMeeting.id, pendingNewParticipants);
        setPendingNewParticipants([]);
      }

      setIsEditing(false);
      setShowMeetingDetails(false);
    } catch (err) {
      console.log("Error updating meeting:", err);
      alert("Failed to update meeting. Please try again.");
    }
  };


  // Broadcast announcement helper functions
  const getActiveBroadcasts = () => {
    const now = new Date();
    const base = broadcastAnnouncements.concat(dynamicBroadcastAnnouncements);
    console.log("basssse", base);

    const active = base.filter((broadcast) => {
      const expiresAt = new Date(broadcast.expiresAt);
      return expiresAt > now && !dismissedBroadcasts.includes(broadcast.id);
    });
    console.log("Active broadcasts:", active.length, active);
    return active;
  };

  const getCurrentBroadcast = () => {
    const activeBroadcasts = getActiveBroadcasts();
    if (activeBroadcasts.length === 0) {
      // console.log("No active broadcasts found");
      return null;
    }
    const current =
      activeBroadcasts[currentBroadcastIndex % activeBroadcasts.length];
    console.log("Current broadcast:", current);
    return current;
  };

  const handleNextBroadcast = () => {
    const activeBroadcasts = getActiveBroadcasts();
    if (activeBroadcasts.length > 1) {
      setCurrentBroadcastIndex((prev) => (prev + 1) % activeBroadcasts.length);
    }
  };

  const handlePreviousBroadcast = () => {
    const activeBroadcasts = getActiveBroadcasts();
    if (activeBroadcasts.length > 1) {
      setCurrentBroadcastIndex(
        (prev) => (prev - 1 + activeBroadcasts.length) % activeBroadcasts.length
      );
    }
  };

  const handleDismissBroadcast = (broadcastId: number) => {
    setDismissedBroadcasts((prev) => [...prev, broadcastId]);
    // Reset index if needed
    const activeBroadcasts = getActiveBroadcasts().filter(
      (b) => b.id !== broadcastId
    );
    if (
      activeBroadcasts.length > 0 &&
      currentBroadcastIndex >= activeBroadcasts.length
    ) {
      setCurrentBroadcastIndex(0);
    }
  };

  const getBroadcastIcon = (type: string) => {
    switch (type) {
      case "event":
        return <CalendarIcon className="text-blue-500" size={20} />;
      case "maintenance":
        return <AlertCircle className="text-orange-500" size={20} />;
      case "feature":
        return <CheckCircle className="text-green-500" size={20} />;
      case "meeting":
        return <Video className="text-purple-500" size={20} />;
      default:
        return <Bell className="text-gray-500" size={20} />;
    }
  };
  const formatRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor(
      (now.getTime() - time.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return time.toLocaleDateString();
  };

  const [meetingDetailsTab, setMeetingDetailsTab] = useState<
    "details" | "security"
  >("details");
  const [isMeetingScheduleCollapsed, setIsMeetingScheduleCollapsed] =
    useState(false);
  const [currentBroadcastIndex, setCurrentBroadcastIndex] = useState(0);
  const [dismissedBroadcasts, setDismissedBroadcasts] = useState<number[]>([]);
  const [securitySettings, setSecuritySettings] = useState({
    requirePassword: false,
    password: '',
    waitingRoom: false,
    muteOnEntry: false,
    screenShare: "host-only",
    recording: false,
    chat: "all",
    participantVideo: true,
    participantAudio: true,
    moderators: [] as string[],
  });
  const [passwordConfirmed, setPasswordConfirmed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);


  // const [darkMode, setDarkMode] = useState(true);
  const [recurring, setRecurring] = useState(false);


  // When checkbox is unchecked, reset to None
  const handleRecurringToggle = (checked: boolean) => {
    setRecurring(checked);
    if (!checked) {
      setRecurringFrequency("None");
      setRecurringDays([]);
    }
  };

  useEffect(() => {
    const update = () =>
      setTime(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleClearNotification = (id: number) => {
    const notif = notifications.find((n) => n.id === id);
    if (notif) {
      setNotifications(notifications.filter((n) => n.id !== id));
      setClearedNotifications([notif, ...clearedNotifications]);
    }
  };
  const handleClearAll = () => {
    setClearedNotifications([...notifications, ...clearedNotifications]);
    setNotifications([]);
  };

  const handleMeetingCardClick = (meeting: any) => {
    setSelectedMeeting(meeting);
    setShowMeetingDetails(true);
    setIsEditing(false);
    setCameFromStatsModal(false);
  };


  const CopyButton = ({ text, darkMode }: { text: string; darkMode: boolean }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
    };

    return (
      <button
        type="button"
        onClick={handleCopy}
        className={` mt-5 -translate-y-1/2 px-2 py-1 text-xs rounded-md font-medium transition-all duration-200 ${copied
          ? "bg-green-500 text-white"
          : "text-white bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90"
          }`}
      >
        {copied ? "Copied" : <Copy size={14} />}
      </button>
    );
  };



  const MeetingButton = ({ meeting, darkMode }: { meeting: any; darkMode: boolean }) => {
    const now = new Date();

    // Helper function to convert 12-hour time to 24-hour format
    const convertTo24Hour = (timeString: string) => {
      // If already in 24-hour format (HH:mm), return as is
      if (!timeString.includes('AM') && !timeString.includes('PM')) {
        return timeString;
      }

      // Handle 12-hour format (e.g., "10:00 AM")
      const [time, period] = timeString.split(' ');
      let [hours, minutes] = time.split(':').map(Number);

      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    try {
      // Combine meeting_date + start_time / end_time into Date objects
      const meetingDate = new Date(meeting.meeting_date);

      // Check if date is valid
      if (isNaN(meetingDate.getTime())) {
        console.error('Invalid meeting date:', meeting.meeting_date);
        return (
          <button
            className={`w-full py-4 px-6 rounded-xl font-semibold text-base transition-all duration-200 opacity-50 cursor-not-allowed ${darkMode ? "bg-white/10 text-white" : "bg-gray-100 text-gray-900"
              }`}
            disabled
          >
            Invalid Date
          </button>
        );
      }

      // Convert times to 24-hour format
      const startTime24 = convertTo24Hour(meeting.start_time);
      const endTime24 = convertTo24Hour(meeting.end_time);

      const [startHours, startMinutes] = startTime24.split(":").map(Number);
      const [endHours, endMinutes] = endTime24.split(":").map(Number);

      // Create Date objects for start and end times
      const startTime = new Date(meetingDate);
      startTime.setHours(startHours, startMinutes, 0, 0);

      const endTime = new Date(meetingDate);
      endTime.setHours(endHours, endMinutes, 0, 0);

      // Determine meeting status
      let status: "upcoming" | "live" | "ended";
      if (now < startTime) {
        status = "upcoming";
      } else if (now > endTime) {
        status = "ended";
      } else {
        status = "live";
      }

      // Format time in readable 12-hour format
      const formatTimeTo12Hour = (timeString: string) => {
        if (!timeString) return 'N/A';

        // If already in 12-hour format, return as is
        if (timeString.includes('AM') || timeString.includes('PM')) {
          return timeString.toUpperCase();
        }

        // Convert from 24-hour format
        const [hours, minutes] = timeString.split(":");
        const date = new Date();
        date.setHours(Number(hours), Number(minutes));
        return date
          .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
          .replace(/am|pm/i, (match) => match.toUpperCase());
      };

      return (
        <button
          className={`w-full py-4 px-6 rounded-xl font-semibold text-base transition-all duration-200 ${
            // status === "live"
            // ?
            darkMode
              ? "bg-gradient-to-r from-pink-600 to-red-500 text-white hover:from-pink-700 hover:to-red-600 shadow-lg"
              : "bg-gradient-to-r from-pink-600 to-red-500 text-white hover:from-pink-700 hover:to-red-600 shadow-lg"
            // : darkMode
            // ? "bg-white/10 text-white/50 cursor-not-allowed"
            // : "bg-gray-100 text-gray-500 cursor-not-allowed"
            }`}
          onClick={(e) => {
            e.stopPropagation();
            // if (status === "live") window.open(meeting.meeting_link, "_blank");
            // if (status === "live") joinMeetingNew(meeting.id);
            joinMeetingNew(meeting.id);

          }}
        // disabled={status !== "live"}
        >
          {/* {status === "upcoming"
          ? `Starts at ${formatTimeTo12Hour(meeting.start_time)}`
          : status === "ended"
          ? "Meeting Ended"
          : "Join Meeting"} */}
          Join Meeting
        </button>
      );
    } catch (error) {
      console.error('Error in MeetingButton:', error, meeting);
      return (
        <button
          className={`w-full py-4 px-6 rounded-xl font-semibold text-base transition-all duration-200 opacity-50 cursor-not-allowed ${darkMode ? "bg-white/10 text-white" : "bg-gray-100 text-gray-900"
            }`}
          disabled
        >
          No Details Available
        </button>
      );
    }
  };

  const [meetingReminders, setMeetingReminders] = useState(false);
  const [reminderMinutes, setReminderMinutes] = useState("00:10:00");
  const [meetingStartNotifications, setMeetingStartNotifications] =
    useState(true);

  const [notificationPermission, setNotificationPermission] = useState<string>(() => {
    try {
      if (typeof window === "undefined") return "unsupported";

      const Notif =
        "Notification" in window ? window.Notification : null;

      if (!Notif || /iPad|iPhone|iPod/.test(navigator.userAgent)) {
        return "unsupported";
      }

      return (
        localStorage.getItem("notification_permission") ||
        Notif.permission ||
        "default"
      );
    } catch {
      return "default";
    }
  });




  const requestBrowserPermissionAndUpdate = async () => {
    // 🚫 iOS → skip completely
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      /iPad|iPhone|iPod/.test(navigator.userAgent)
    ) {
      console.log("🚫 iOS browser notifications not supported");

      setNotificationPermission("unsupported");
      localStorage.setItem("notification_permission", "unsupported");
      return;
    }

    try {
      const Notif = window.Notification;
      const res = await Notif.requestPermission();

      setNotificationPermission(res);
      localStorage.setItem("notification_permission", res);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';

      if (res === "granted") {
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/notification-preferences`,
          {
            method: "POST",
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              meeting_reminder_enabled: true,
              meeting_reminder_before_start: true,
              meeting_reminder_time: reminderMinutes || "00:10:00"
            })
          }
        );
      }
    } catch (err) {
      console.error("Permission error:", err);
    }
  };


  useEffect(() => {
    if (notificationPermission === "default") {
      requestBrowserPermissionAndUpdate();
    }
  }, []);



  const handleEditClick = () => {
    setIsEditing(true);
    setPendingNewParticipants([]);

    const meetingDate = new Date(selectedMeeting.meeting_date);

    // Helper to convert "18:45:00" → "6:45 PM"
    const formatTimeTo12Hour = (timeStr: any) => {
      if (!timeStr) return "";
      const [hour, minute] = timeStr.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 === 0 ? 12 : hour % 12;
      const displayMinute = String(minute).padStart(2, "0");
      return `${displayHour}:${displayMinute} ${period}`;
    };

    setEditForm({
      title: selectedMeeting.title || "",
      description: selectedMeeting.description || "",
      time: selectedMeeting.time || "9:00 AM - 10:00 AM",
      recurring: selectedMeeting.is_recurring || "None",
      host_name: selectedMeeting.host_name || "John Smith",
      room: selectedMeeting.room || "Room 1432",
      status: selectedMeeting.status || "Scheduled",
      participants: selectedMeeting.participants || [],
      date: meetingDate,
      startTime: selectedMeeting.start_time
        ? formatTimeTo12Hour(selectedMeeting.start_time)
        : "10:00 AM",
      endTime: selectedMeeting.end_time
        ? formatTimeTo12Hour(selectedMeeting.end_time)
        : "11:00 AM",
      recurringStartDate: selectedMeeting.recurring_start_date || null,
      recurringEndDate: selectedMeeting.recurring_end_date || null,
      recurringFrequency: selectedMeeting.repeat_status || "None",
      repeatDays: selectedMeeting.repeat_days || [],
    });

    // Load security settings from the meeting
    setSecuritySettings({
      requirePassword: selectedMeeting.password_protection || false,
      password: selectedMeeting.meeting_password || '',
      waitingRoom: selectedMeeting.waiting_room || false,
      muteOnEntry: selectedMeeting.mute_on_entry || false,
      screenShare: "host-only",
      recording: selectedMeeting.allow_recording || false,
      chat: "all",
      participantVideo: true,
      participantAudio: true,
      moderators: [],
    });

    setRecurringFrequency(selectedMeeting.repeat_status || "None");
    setRecurringStartDate(
      selectedMeeting.recurring_start_date
        ? new Date(selectedMeeting.recurring_start_date)
        : null
    );
    setRecurringEndDate(
      selectedMeeting.recurring_end_date
        ? new Date(selectedMeeting.recurring_end_date)
        : null
    );
    setRecurringDays(selectedMeeting.repeat_days || []);
  };



  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      title: "",
      description: "",
      time: "",
      recurring: "",
      host_name: "",
      room: "",
      status: "",
      participants: [],
      date: new Date(),
      startTime: "10:00 AM",
      endTime: "11:00 AM",
      recurringStartDate: null,
      recurringEndDate: null,
      recurringFrequency: "None",
      repeatDays: [],
    });
  };

  const handleAddInvite = () => {
    if (inviteEmail && inviteEmail.includes("@")) {
      setInviteList([...inviteList, inviteEmail]);
      setInviteEmail("");
    }
  };

  const handleRemoveInvite = (email: string) => {
    setInviteList(inviteList.filter((e) => e !== email));
  };

  const handleSendInvites = () => {
    if (inviteList.length > 0) {
      sendInvites(selectedMeeting.id, inviteList);
      setShowInviteModal(false);
      setInviteList([]);
    }
  };

  const formatTimeTo12Hour = (timeString: string) => {
    // Handle null, undefined, or empty string
    if (!timeString || typeof timeString !== 'string') {
      return 'N/A';
    }

    try {
      // Check if it's already in 12-hour format (contains AM/PM)
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }

      // Handle both HH:mm and HH:mm:ss formats
      const timeParts = timeString.split(':');
      if (timeParts.length < 2) {
        console.warn('Invalid time format:', timeString);
        return timeString;
      }

      const hours = Number(timeParts[0]);
      const minutes = Number(timeParts[1]);

      // Validate hours and minutes
      if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        console.warn('Invalid time values:', timeString);
        return timeString;
      }

      const date = new Date();
      date.setHours(hours, minutes, 0, 0);

      return date
        .toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        })
        .replace(/am|pm/i, (match) => match.toUpperCase());
    } catch (error) {
      console.error('Error formatting time:', error, timeString);
      return timeString;
    }
  };




  // Function to format date to readable format
  const formatDateToReadable = (dateString: string) => {
    if (!dateString || typeof dateString !== 'string') {
      return 'N/A';
    }

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return dateString;
    }
  };



  function filterMeetingsByDate(meetings: any[], selectedFilter: string) {
    if (!selectedFilter || selectedFilter === "7days") return meetings; // default
    const now = new Date();

    return meetings.filter((meeting) => {
      const createdAt = new Date(meeting.meeting_date);
      console.log("createdAt", createdAt);


      switch (selectedFilter) {
        case "today": {
          const today = new Date();
          return createdAt.toDateString() === today.toDateString();
        }

        case "yesterday": {
          const yesterday = new Date();
          yesterday.setDate(now.getDate() - 1);
          return createdAt.toDateString() === yesterday.toDateString();
        }

        case "7days":
          return createdAt >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

        case "14days":
          return createdAt >= new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);

        case "1month": {
          const oneMonthAgo = new Date();
          oneMonthAgo.setMonth(now.getMonth() - 1);
          return createdAt >= oneMonthAgo;
        }

        case "1year": {
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(now.getFullYear() - 1);
          return createdAt >= oneYearAgo;
        }

        default:
          return true;
      }
    });
  }


  function useFilteredMeetings(contextMeetings: any[], meetingsTab: string, selectedDateFilter: string, user: any) {
    const filteredMeetings = useMemo(() => {
      // Step 1: Filter by tab
      let tabFiltered = contextMeetings.filter((meeting) => {
        switch (meetingsTab) {
          case "all":
            // Show both created meetings and accepted participant meetings
            return true;
          case "created":
            return meeting.created_by.id === user?.id;
          case "completed":
            return meeting.completed_status;
          case "cancelled":
            return meeting.canceled_meeting;
          default:
            return true;
        }
      });

      // Step 2: Filter by date
      return filterMeetingsByDate(tabFiltered, selectedDateFilter);
    }, [contextMeetings, meetingsTab, selectedDateFilter, user]);

    return filteredMeetings;
  }

  const filteredMeetings = useFilteredMeetings(contextMeetings, meetingsTab, selectedDateFilter, user);


  const extractMeetingId = (input: string): string => {
    const trimmedInput = input.trim();

    // If it's a plain number, return it as is
    if (/^\d+$/.test(trimmedInput)) {
      return trimmedInput;
    }

    // Try to extract meeting slug/ID from URL
    try {
      const url = new URL(trimmedInput);
      const pathSegments = url.pathname.split('/').filter(segment => segment.length > 0);
      if (pathSegments.length > 0) {
        return pathSegments[pathSegments.length - 1];
      }
    } catch (e) {
      // Not a valid URL, might be just a slug
      if (/^[a-zA-Z0-9-_]+$/.test(trimmedInput)) {
        return trimmedInput;
      }
    }

    throw new Error('Invalid meeting link or room ID format');
  };


  const [error, setError] = useState('');



  const handleJoinMeeting = async () => {
    const roomInput = joinInput.trim();
    console.log("roomInput---", roomInput);

    if (!roomInput) {
      setError("Please enter a meeting link or room ID");
      setTimeout(() => setError(""), 3000);
      return;
    }

    try {
      const meetingIdentifier = extractMeetingId(roomInput);
      console.log("meetingIdentifier---", meetingIdentifier);

      let meetingUrl = "";

      //  Step 1: Numeric ID — use joinMeeting API (proper server-side moderator check)
      if (/^\d+$/.test(meetingIdentifier)) {
        const meetingData = await getMeetingById(Number(meetingIdentifier));

        if (!meetingData) {
          setError("Meeting not found. Please check your meeting link or ID.");
          setTimeout(() => setError(""), 3000);
          return;
        }

        // Use joinMeeting which calls backend /:meetingId/join
        // This endpoint checks if the user is creator or designated moderator server-side
        try {
          const result = await joinMeeting(meetingData.meeting.id);
          meetingUrl = result.meeting.meeting_link || meetingData.meeting.meeting_link;
          console.log("Joined meeting with role:", result.userRole);
        } catch (joinErr: any) {
          // If password is required, handle it
          if (joinErr.message?.includes("password")) {
            openPasswordModal(meetingData.meeting.id);
            return;
          }
          throw joinErr;
        }
      } else {
        //  Step 2: Non-numeric identifier (URL or slug) — generate token with default role
        const isModerator = false;
        await jitsiToken(0, isModerator);

        //  Construct meeting URL (JWT is set via HttpOnly cookie by the backend)
        if (roomInput.startsWith("http")) {
          const url = new URL(roomInput);
          url.searchParams.delete("jwt"); // Remove any existing jwt param
          meetingUrl = url.toString();
        } else {
          meetingUrl = `${process.env.REACT_APP_BACKEND_URL}/${meetingIdentifier}`;
        }
      }

      console.log("Final meeting URL:", meetingUrl);
      window.open(meetingUrl, "_blank");

      //  Reset UI
      setShowJoinMeeting(false);
      setJoinInput("");
    } catch (err) {
      console.error("Failed to join meeting:", err);
      setError(`${err instanceof Error ? err.message : "Please try again"}`);
      setTimeout(() => setError(""), 3000);
    }
  };





  const handleStatClick = (
    statType: "scheduled" | "attended" | "cancelled"
  ) => {
    setSelectedStatType(statType);
    setShowStatsModal(true);
  };

  const handleStatsMeetingDetails = (meeting: any) => {
    setSelectedMeeting(meeting);
    setShowMeetingDetails(true);
    setShowStatsModal(false);
    setIsEditing(false);
    setCameFromStatsModal(true);
  };

  const handleBackToStatsModal = () => {
    setShowMeetingDetails(false);
    setShowStatsModal(true);
    setCameFromStatsModal(false);
  };

  const handleModeratorToggle = (email: string) => {
    setSecuritySettings((prev) => ({
      ...prev,
      moderators: prev.moderators.includes(email)
        ? prev.moderators.filter((m) => m !== email)
        : [...prev.moderators, email],
    }));
  };

  const handleJoinMeetingClickOutside = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowJoinMeeting(false);
    }
  };

  const handleCreateMeetingClickOutside = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowCreateMeeting(false);
    }
  };

  // Generate time options in 15-minute intervals with AM/PM format
  const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
    const hour = Math.floor(i / 4);
    const minute = (i % 4) * 15;
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = String(minute).padStart(2, "0");
    return `${displayHour}:${displayMinute} ${period}`;
  });

  return (
    <>
      <div
        className={`min-h-screen ${darkMode
          ? "bg-gradient-to-br from-[#181824] to-[#232336]"
          : "bg-gradient-to-br from-gray-50 to-blue-50"
          }`}
      >
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
        <main
          className={`flex-1 px-2 py-8 md:px-12 md:py-10 transition-all duration-200 ${collapsed ? "ml-20" : "ml-20 md:ml-52"
            }`}
        >
          {/* Top Bar */}
          <NavBar />
          {/* Quick Actions */}
          <div className="mb-8 mt-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
              <h3
                className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                  }`}
              >
                Quick Actions
              </h3>
            </div>

            {/* Modern Horizontal Cards */}
            <div className="flex flex-col md:flex-row gap-3">
              {/* Create Meeting */}
              <button
                className={`group flex-1 flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg relative overflow-hidden ${darkMode
                  ? "bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10"
                  : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50 shadow-sm"
                  }`}
                onClick={() => setShowCreateMeeting(true)}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Plus size={20} className="text-white" />
                </div>
                <div className="flex-1 text-left">
                  <h4
                    className={`text-sm font-semibold mb-1 ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Create Meeting
                  </h4>
                  <p
                    className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                  >
                    Start an instant video call
                  </p>
                </div>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-pink-600 to-red-500 opacity-20 rounded-full -translate-y-10 translate-x-10"></div>
              </button>

              {/* Join Meeting */}
              <button
                className={`group flex-1 flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg relative overflow-hidden ${darkMode
                  ? "bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10"
                  : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50 shadow-sm"
                  }`}
                onClick={() => setShowJoinMeeting(true)}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-purple-500 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <LogIn size={20} className="text-white" />
                </div>
                <div className="flex-1 text-left">
                  <h4
                    className={`text-sm font-semibold mb-1 ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Join Meeting
                  </h4>
                  <p
                    className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                  >
                    Enter with room ID or link
                  </p>
                </div>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-500 opacity-20 rounded-full -translate-y-10 translate-x-10"></div>
              </button>

              {/* Schedule Meeting */}
              <button
                className={`group flex-1 flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg relative overflow-hidden ${darkMode
                  ? "bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10"
                  : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50 shadow-sm"
                  }`}
                onClick={() => setShowScheduleMeeting(true)}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-green-600 to-teal-500 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <CalendarIcon size={20} className="text-white" />
                </div>
                <div className="flex-1 text-left">
                  <h4
                    className={`text-sm font-semibold mb-1 ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Schedule Meeting
                  </h4>
                  <p
                    className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                  >
                    Plan for later with invites
                  </p>
                </div>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-600 to-teal-500 opacity-20 rounded-full -translate-y-10 translate-x-10"></div>
              </button>
            </div>
          </div>

          {/* Meetings Today */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
                <h3
                  className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Meeting Schedule
                </h3>
              </div>
              <button
                onClick={() =>
                  setIsMeetingScheduleCollapsed(!isMeetingScheduleCollapsed)
                }
                className={`p-2 rounded-lg transition-all duration-200 ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
                  }`}
              >
                {isMeetingScheduleCollapsed ? (
                  <ChevronDown
                    className={`${darkMode ? "text-white/70" : "text-gray-600"
                      }`}
                    size={20}
                  />
                ) : (
                  <ChevronUp
                    className={`${darkMode ? "text-white/70" : "text-gray-600"
                      }`}
                    size={20}
                  />
                )}
              </button>
            </div>
            {!isMeetingScheduleCollapsed && (
              <div
                className={`rounded-2xl p-6 border ${darkMode
                  ? "bg-gradient-to-r from-blue-600/10 to-purple-500/10 border-white/10"
                  : "bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200"
                  }`}
              >
                {/* Meeting Summary */}
                <div className="flex items-center justify-between mb-2">
                  <div className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                    You have {todayMeetings.length} meeting{todayMeetings.length !== 1 ? "s" : ""} today!
                  </div>
                </div>

                <div className={`text-sm mb-4 ${darkMode ? "text-white/60" : "text-gray-600"}`}>
                  {nextMeeting ? timeLeft : "No upcoming meetings today"}
                </div>

                {nextMeeting && (
                  <div className="flex items-center gap-4 text-sm mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <span className={`${darkMode ? "text-white/70" : "text-gray-700"}`}>
                        Next: {nextMeeting.title} ({formatTime(nextMeeting.meeting_date.split('T')[0] + 'T' + nextMeeting.start_time)})
                      </span>
                    </div>
                  </div>
                )}

                {/* Company Broadcast Announcement */}
                {(() => {
                  const currentBroadcast = getCurrentBroadcast();
                  const activeBroadcasts = getActiveBroadcasts();
                  console.log(
                    "Rendering broadcast section. Current:",
                    currentBroadcast,
                    "Active count:",
                    activeBroadcasts.length
                  );

                  if (!currentBroadcast) {
                    return (
                      <div
                        className={`relative rounded-lg p-4 pl-6 ${darkMode ? "bg-white/5" : "bg-gray-50/50"
                          }`}
                      >
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-pink-500 to-red-500 rounded-l-lg"></div>
                        <p
                          className={`text-sm ${darkMode ? "text-white/70" : "text-gray-700"
                            }`}
                        >
                          No company announcements at this time.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div
                      className={`relative rounded-lg p-4 pl-6 ${darkMode ? "bg-white/5" : "bg-gray-50/50"
                        }`}
                    >
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-pink-500 to-red-500 rounded-l-lg"></div>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          {getBroadcastIcon(currentBroadcast.type)}
                          <div>
                            <h4
                              className={`font-semibold text-sm ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {currentBroadcast.title}
                            </h4>
                            <div className="flex items-center gap-2 text-xs mt-1">
                              <span
                                className={`${darkMode ? "text-white/60" : "text-gray-600"
                                  }`}
                              >
                                {currentBroadcast.author}
                              </span>
                              <span
                                className={`${darkMode ? "text-white/40" : "text-gray-400"
                                  }`}
                              >
                                •
                              </span>
                              <span
                                className={`${darkMode ? "text-white/60" : "text-gray-600"
                                  }`}
                              >
                                {formatRelativeTime(currentBroadcast.timestamp)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {activeBroadcasts.length > 1 && (
                            <>
                              <button
                                onClick={handlePreviousBroadcast}
                                className={`p-1 rounded-full transition-all duration-200 ${darkMode
                                  ? "hover:bg-white/10 text-white/70 hover:text-white"
                                  : "hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                                  }`}
                                title="Previous announcement"
                              >
                                <ChevronLeft size={14} />
                              </button>
                              <span
                                className={`text-xs px-2 ${darkMode ? "text-white/60" : "text-gray-500"
                                  }`}
                              >
                                {currentBroadcastIndex + 1}/
                                {activeBroadcasts.length}
                              </span>
                              <button
                                onClick={handleNextBroadcast}
                                className={`p-1 rounded-full transition-all duration-200 ${darkMode
                                  ? "hover:bg-white/10 text-white/70 hover:text-white"
                                  : "hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                                  }`}
                                title="Next announcement"
                              >
                                <ChevronRight size={14} />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() =>
                              handleDismissBroadcast(currentBroadcast.id)
                            }
                            className={`p-1 rounded-full transition-all duration-200 ${darkMode
                              ? "hover:bg-white/10 text-white/70 hover:text-white"
                              : "hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                              } ml-1`}
                            title="Dismiss announcement"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                      <p
                        className={`text-sm leading-relaxed ${darkMode ? "text-white/80" : "text-gray-700"
                          }`}
                      >
                        {currentBroadcast.message}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          {/* Meetings Tab Menu */}
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <div
              className={`flex sm:flex-row flex-col  gap-1 mb-4 rounded-lg p-1 sm:w-fit w-full ${darkMode ? "bg-white/5" : "bg-gray-100"
                }`}
            >
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${meetingsTab === "all"
                  ? "bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-lg"
                  : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                onClick={() => setMeetingsTab("all")}
              >
                All Meetings
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${meetingsTab === "created"
                  ? "bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-lg"
                  : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                onClick={() => setMeetingsTab("created")}
              >
                Meetings You Created
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${meetingsTab === "completed"
                  ? "bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-lg"
                  : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                onClick={() => setMeetingsTab("completed")}
              >
                Completed Meetings
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${meetingsTab === "cancelled"
                  ? "bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-lg"
                  : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                onClick={() => setMeetingsTab("cancelled")}
              >
                Canceled Meetings
              </button>
            </div>

            <div className="relative">
              <button
                onClick={() => setShowDateFilter(!showDateFilter)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border ${darkMode
                  ? "border-white/10 text-white/80 hover:bg-white/10"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <CalendarIcon size={16} />
                <span>
                  {dateFilterOptions.find((o) => o.value === selectedDateFilter)
                    ?.label || "Today"}
                </span>
                <ChevronDown size={14} />
              </button>
              {showDateFilter && (
                <div
                  className={`absolute right-0 mt-2 w-44 rounded-lg shadow-2xl border z-20 ${darkMode
                    ? "bg-[#232336] border-white/10"
                    : "bg-white border-gray-200"
                    }`}
                >
                  {dateFilterOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSelectedDateFilter(opt.value);
                        setShowDateFilter(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm ${darkMode
                        ? "text-white/80 hover:bg-white/10"
                        : "text-gray-700 hover:bg-gray-100"
                        }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Placeholder Meeting Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {meetingsLoading ? (
              <div className="col-span-full text-center py-12">Loading meetings...</div>
            )
              // : meetingsError ? (
              //   <div className="col-span-full text-center py-12 text-red-500">{meetingsError}</div>
              // )
              : filteredMeetings.length === 0 ? (
                <div className="col-span-full text-center py-12">No meetings found</div>
              ) : (
                filteredMeetings.map((meeting) => (
                  <div
                    className={`group relative overflow-hidden rounded-2xl border transition-all duration-300 hover:scale-105 hover:shadow-xl cursor-pointer ${darkMode
                      ? "bg-gradient-to-br from-white/5 to-white/2 border-white/10 hover:border-white/20 hover:bg-white/10"
                      : "bg-white border-gray-200/60 hover:border-gray-300 hover:shadow-lg"
                      }`}
                    onClick={() => handleMeetingCardClick(meeting)}
                  >
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    {/* Status Indicator */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-600 to-red-500"></div>

                    <div className="relative z-10 p-6">
                      {/* Header */}
                      <div className="mb-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between">
                              <h3
                                className={`font-bold text-xl leading-tight mb-2 ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                              >
                                {meeting.title}
                              </h3>
                              {/* Status Badge - Compact */}
                              <div className="flex-shrink-0 ml-3">
                                {meeting.canceled_meeting ? (
                                  <span className="px-3 py-1.5 bg-red-100 text-red-600 text-xs rounded-full font-semibold border border-red-200">
                                    Cancelled
                                  </span>
                                ) : meeting.completed_status ? (
                                  <span className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full font-semibold border border-gray-200">
                                    Completed
                                  </span>
                                ) : (
                                  <span className="px-3 py-1.5 bg-green-100 text-green-700 text-xs rounded-full font-semibold border border-green-200">
                                    {meeting.meeting_type}
                                  </span>
                                )}
                              </div>
                            </div>

                            {meeting.description && (
                              <p
                                className={`text-sm mb-3 truncate max-w-full ${darkMode ? "text-white/60" : "text-gray-600"
                                  }`}
                              >
                                {meeting.description}
                              </p>
                            )}


                            {/* Date and Time */}
                            <div className="flex items-center gap-3 text-sm my-3">
                              <div
                                className={`flex items-center gap-1.5 ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                <CalendarIcon size={16} />
                                <span className="font-medium">
                                  {formatDate(meeting.meeting_date)}
                                </span>
                              </div>
                              <span
                                className={`${darkMode ? "text-white/40" : "text-gray-400"
                                  }`}
                              >
                                •
                              </span>
                              <div
                                className={`flex items-center gap-1.5 ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                <Clock size={16} />
                                {/* <span>{formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}</span> */}
                                {/* {formatTime(`${meeting.meeting_date}T${meeting.start_time}`)} - {formatTime(`${meeting.date}T${meeting.end_time}`)} */}
                                {meeting.meeting_date !== null ? (
                                  <span>
                                    {formatTime(meeting.meeting_date.split('T')[0] + 'T' + meeting.start_time)} -
                                    {formatTime(meeting.meeting_date.split('T')[0] + 'T' + meeting.end_time)}
                                  </span>
                                ) : <></>}
                              </div>
                            </div>

                            {/* Host */}
                            <div className="flex items-center gap-3 text-sm">
                              <div
                                className={`flex items-center gap-1.5 ${darkMode ? "text-white/60" : "text-gray-500"
                                  }`}
                              >
                                <User size={16} />
                                <span className="truncate">
                                  {meeting.createdById === user?.id ? 'You' : meeting.created_by.first_name}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Participants - Left Pictures, Right Count */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex -space-x-2">
                          {(meeting?.participantDetails || [])
                            .slice(0, 3)
                            .map((participant: any, j: any) => {
                              const avatar = getParticipantAvatar(participant, j);
                              const hasReal = hasRealProfile(participant);

                              return (
                                <div key={j} className="relative group">
                                  <img
                                    src={avatar}
                                    alt={getParticipantDisplayName(participant)}
                                    className="w-8 h-8 rounded-full border border-white object-cover bg-gray-200 shadow-sm hover:scale-110 transition-transform duration-200"
                                    title={getParticipantDisplayName(participant)}
                                  />
                                  {hasReal && (
                                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 border border-white rounded-full"></div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                        <div
                          className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          {meeting?.participantDetails?.length || 1} participant{meeting?.participantDetails?.length !== 1 ? "s" : ""}
                        </div>
                      </div>

                      {/* Action Button */}
                      {/* Action Button */}
                      {meeting.canceled_meeting || meeting.completed_status ? (
                        // Use a common styling for Cancelled/Completed meetings (View Details)
                        <button
                          className={`w-full py-4 px-6 rounded-xl font-semibold text-base transition-all duration-200 
      ${darkMode
                              ? "bg-white/10 text-white/80 hover:bg-white/20 hover:text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          // NOTE: onClick is intentionally omitted/empty here. The click handler is on the parent div/card.
                          // However, for consistency and clear intention, let's keep the logic simple:
                          onClick={(e) => {
                            // Prevent click from propagating ONLY if we were to add extra logic here.
                            // For 'View Details', let the card handle opening the modal.
                            // e.stopPropagation(); // Keeping this commented out lets the parent card's onClick handle the modal opening.
                          }}
                        >
                          View Details
                        </button>
                      ) : (
                        // Use MeetingButton for live/scheduled meetings (Join Meeting)
                        <MeetingButton meeting={meeting} darkMode={darkMode} />
                      )}
                    </div>
                  </div>
                ))
              )}
          </div>

          {/* Meeting Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loadingMeetings && (
              <div className="col-span-full flex items-center justify-center py-12">
                <div
                  className={`text-center ${darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                >
                  <div
                    className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2 ${darkMode ? "border-white" : "border-gray-600"
                      }`}
                  ></div>
                  Loading meetings...
                </div>
              </div>
            )}
            {meetingsError && (
              <div className="col-span-full flex items-center justify-center py-12">
                <div className="text-red-600 text-center">
                  <AlertCircle size={24} className="mx-auto mb-2" />
                  {meetingsError}
                </div>
              </div>
            )}
            {openMeetings.map((meeting, idx) => (
              <div
                key={meeting.meeting_id || idx}
                className={`group relative overflow-hidden rounded-2xl p-6 border shadow-lg cursor-pointer transition-all duration-300 hover:scale-105 ${darkMode
                  ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                  : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                onClick={() => handleMeetingCardClick(meeting)}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`font-bold text-lg ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {meeting.title || "Untitled Meeting"}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-orange-600/20 text-orange-400 text-xs rounded-full font-medium">
                        Live
                      </span>
                      <button
                        className={`p-1 rounded transition ${darkMode
                          ? "text-white/40 hover:text-white hover:bg-white/10"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                          }`}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div
                      className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/80" : "text-gray-700"
                        }`}
                    >
                      <User
                        size={16}
                        className={`${darkMode ? "text-white" : "text-gray-600"
                          }`}
                      />
                      <span>
                        Hosted by {meeting.host_name || "Unknown Host"}
                      </span>
                    </div>
                    <div
                      className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                    >
                      <Hash
                        size={16}
                        className={`${darkMode ? "text-white" : "text-gray-600"
                          }`}
                      />
                      <span>Room 1432</span>
                    </div>
                    <div
                      className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/60" : "text-gray-500"
                        }`}
                    >
                      <Clock
                        size={16}
                        className={`${darkMode ? "text-white" : "text-gray-600"
                          }`}
                      />
                      <span>
                        {meeting.start_time
                          ? new Date(meeting.start_time).toLocaleTimeString(
                            [],
                            { hour: "2-digit", minute: "2-digit" }
                          )
                          : "TBD"}
                      </span>
                    </div>
                  </div>

                  <button
                    className="w-full bg-gradient-to-r from-pink-600 to-red-500 text-white font-semibold py-3 rounded-xl hover:scale-105 transition-all duration-200 shadow-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/meeting-new`);
                    }}
                  >
                    Join Live Meeting
                  </button>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
      {/* Modal Overlay for Schedule Meeting */}
      {showScheduleMeeting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm overflow-hidden">
          <CreateMeeting
            darkMode={darkMode}
            onClose={() => setShowScheduleMeeting(false)}
            onCreated={(meeting) => {
              setShowScheduleMeeting(false);
              // Refresh open meetings
              setLoadingMeetings(true);
              fetch("/api/meetings/open")
                .then((res) => res.json())
                .then((data) => setOpenMeetings(data.meetings || []))
                .finally(() => setLoadingMeetings(false));
              // Navigate to the new meeting room
              if (meeting && meeting.meetingId) {
                navigate(`/meeting-new`);
              }
            }}
          />
        </div>
      )}
      {/* Join Meeting Modal */}
      {showJoinMeeting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm overflow-hidden"
          onClick={handleJoinMeetingClickOutside}
        >
          <div
            className={`rounded-3xl shadow-2xl border w-[95vw] max-w-3xl relative max-h-[100vh] overflow-hidden ${darkMode
              ? "bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10"
              : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
              }`}
          >
            {/* Close Button */}
            <button
              className={`absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                }`}
              aria-label="Close"
              onClick={() => setShowJoinMeeting(false)}
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div className="relative px-8 pt-8 pb-6">
              <div className="text-center flex">
                <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-purple-500 shadow-lg">
                  <LogIn size={24} className="text-white" />
                </div>
                <div className="flex-1 flex-col ml-5 text-left gap-5">
                  <h2
                    className={`text-xl font-bold mb-1 ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Join Meeting
                  </h2>
                  <p
                    className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                  >
                    Enter meeting room ID or paste invitation link
                  </p>
                </div>
              </div>
              {error && (
                <div
                  className={`mx-5 mb-6 p-3 rounded-xl text-center text-sm ${darkMode ? "bg-red-600/20 border border-red-500/30 text-red-400" : "bg-red-50 border border-red-200 text-red-600"
                    }`}
                >
                  <AlertCircle size={18} className="mx-auto mb-2" />
                  {error}
                </div>
              )}
            </div>

            {/* Content Area */}
            <div className="px-8 pb-6">
              <div className="space-y-6">
                {/* Input */}
                <div className="group">
                  <label
                    className={`block mb-2 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                      }`}
                  >
                    Meeting Link or Room ID
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Room ID or meeting link..."
                      className={`w-full rounded-xl px-4 py-3 transition-all duration-200 text-base focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                        ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
                        : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                        }`}
                      value={joinInput}
                      onChange={(e) => setJoinInput(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleJoinMeeting()
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div
              className={`flex gap-4 p-6 pt-4 pb-6 border-t ${darkMode ? "border-white/10" : "border-gray-200"
                }`}
            >
              <button
                type="button"
                onClick={() => setShowJoinMeeting(false)}
                className={`px-8 py-3.5 rounded-xl transition-all duration-200 text-sm font-medium ${darkMode
                  ? "border border-white/20 text-white hover:bg-white/10"
                  : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleJoinMeeting}
                className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 text-white py-3.5 rounded-xl font-semibold hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                disabled={!joinInput.trim()}
              >
                {meetingsLoading ? <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Joining Meeting...
                </div> : 'Join Meeting'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Create Meeting Modal */}
      {showCreateMeeting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm overflow-hidden"
          onClick={handleCreateMeetingClickOutside}
        >
          <div
            className={`rounded-3xl shadow-2xl border w-[95vw] max-w-3xl relative max-h-[100vh] overflow-hidden ${darkMode
              ? "bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10"
              : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
              }`}
          >
            {/* Close Button */}
            <button
              className={`absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                }`}
              aria-label="Close"
              onClick={() => setShowCreateMeeting(false)}
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div className="relative px-8 pt-8 pb-6">
              <div className="text-center flex">
                <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-gradient-to-br from-pink-600 to-red-500 shadow-lg">
                  <Plus size={24} className="text-white" />
                </div>
                <div className="flex-1 flex-col ml-5 text-left gap-5">
                  <h2
                    className={`text-xl font-bold mb-1 ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Create Meeting
                  </h2>
                  <p
                    className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                  >
                    Start an instant video call
                  </p>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div
              className={`flex gap-1 mx-8 mb-4 rounded-xl p-1 ${darkMode ? "bg-white/5" : "bg-gray-100"
                }`}
            >
              <button
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${scheduleModalTab === "details"
                  ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                  : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                onClick={() => setScheduleModalTab("details")}
              >
                Details
              </button>
              <button
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${scheduleModalTab === "security"
                  ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                  : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                onClick={() => setScheduleModalTab("security")}
              >
                Security
              </button>
            </div>

            {/* Content Area */}
            <div className="px-8 pb-6 overflow-y-auto max-h-[45vh] scrollbar-hide">
              {scheduleModalTab === "details" && (
                <div className="space-y-5">
                  {/* Meeting Title */}
                  <div className="group">
                    <label
                      className={`block mb-2 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                        }`}
                    >
                      Meeting Title
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Enter meeting title..."
                        className={`w-full rounded-xl px-4 py-3 transition-all duration-200 text-base focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                          ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
                          : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                          }`}
                        value={createMeetingForm.title}
                        onChange={(e) => {
                          const newTitle = e.target.value;
                          setCreateMeetingForm({ ...createMeetingForm, title: newTitle });

                          // Generate new link when title changes
                          if (newTitle.trim()) {
                            setLink(generateMeetingLink(newTitle));
                          } else {
                            setLink("");
                          }
                        }}
                      />
                    </div>
                    {showLoginError && (
                      <div className={`p-4 border rounded-xl mt-2 ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <AlertCircle size={20} className="text-red-500" />
                          </div>
                          <div className="ml-2 text-sm text-red-500">{showLoginError}</div>
                          {/* <div className="flex ml-4" > */}
                          <button
                            className="ml-auto text-sm text-white hover:text-white font-medium border border-white/20 hover:border-white rounded-full px-3 py-1 transition-all duration-200 bg-green-500 hover:bg-green-600"
                            onClick={() => navigate('/login')}
                          >
                            Login
                          </button>
                          {/* </div> */}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {scheduleModalTab === "security" && (
                <div className="space-y-5">
                  {/* Security Settings */}
                  <div
                    className={`rounded-xl p-4 border ${darkMode
                      ? "bg-white/5 border-white/10"
                      : "bg-gray-50 border-gray-200"
                      }`}
                  >
                    <div className="space-y-4">
                      {/* Password Protection */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4
                            className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            Password Protection
                          </h4>
                          <p
                            className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          >
                            Require a password to join the meeting
                          </p>
                        </div>
                        <button
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.requirePassword
                            ? "bg-gradient-to-r from-pink-500 to-red-500"
                            : darkMode
                              ? "bg-white/20"
                              : "bg-gray-300"
                            }`}
                          onClick={() =>
                            setSecuritySettings({
                              ...securitySettings,
                              requirePassword:
                                !securitySettings.requirePassword,
                            })
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.requirePassword
                              ? "translate-x-6"
                              : "translate-x-1"
                              } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                          />
                        </button>
                      </div>

                      {securitySettings.requirePassword && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <label
                              className={`text-sm font-medium whitespace-nowrap ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              Meeting Password
                            </label>
                            <div className="w-64 relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter Your Password"
                                className={`w-full rounded-lg px-3 py-2 pr-20 transition-all duration-200 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                                  ? "bg-white/10 border border-white/20 text-white placeholder-white/40"
                                  : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                                  }`}
                                value={securitySettings.password}
                                // readOnly
                                onChange={(e) => setSecuritySettings({
                                  ...securitySettings,
                                  password: e.target.value
                                })}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${darkMode
                                  ? "text-white/60 hover:text-white"
                                  : "text-gray-400 hover:text-gray-600"
                                  }`}
                              >
                                {showPassword ? (
                                  <Eye size={16} />
                                ) : (
                                  <EyeOff size={16} />
                                )}
                              </button>
                            </div>
                            {/* Copy button */}
                            <CopyButton
                              text={securitySettings.password}
                              darkMode={darkMode}
                            />
                          </div>
                        </div>
                      )}

                      {/* Waiting Room */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4
                            className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            Waiting Room
                          </h4>
                          <p
                            className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          >
                            Participants must be admitted by the host
                          </p>
                        </div>
                        <button
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.waitingRoom
                            ? "bg-gradient-to-r from-pink-500 to-red-500"
                            : darkMode
                              ? "bg-white/20"
                              : "bg-gray-300"
                            }`}
                          onClick={() =>
                            setSecuritySettings({
                              ...securitySettings,
                              waitingRoom: !securitySettings.waitingRoom,
                            })
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.waitingRoom
                              ? "translate-x-6"
                              : "translate-x-1"
                              } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                          />
                        </button>
                      </div>

                      {/* Mute on Entry */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4
                            className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            Mute on Entry
                          </h4>
                          <p
                            className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          >
                            Automatically mute participants when they join
                          </p>
                        </div>
                        <button
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.muteOnEntry
                            ? "bg-gradient-to-r from-pink-500 to-red-500"
                            : darkMode
                              ? "bg-white/20"
                              : "bg-gray-300"
                            }`}
                          onClick={() =>
                            setSecuritySettings({
                              ...securitySettings,
                              muteOnEntry: !securitySettings.muteOnEntry,
                            })
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.muteOnEntry
                              ? "translate-x-6"
                              : "translate-x-1"
                              } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                          />
                        </button>
                      </div>

                      {/* Allow Recording */}
                      {/* <div className="flex items-center justify-between">
                        <div>
                          <h4
                            className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            Allow Recording
                          </h4>
                          <p
                            className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          >
                            Participants can record the meeting
                          </p>
                        </div>
                        <button
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.recording
                            ? "bg-gradient-to-r from-pink-500 to-red-500"
                            : darkMode
                              ? "bg-white/20"
                              : "bg-gray-300"
                            }`}
                          onClick={() =>
                            setSecuritySettings({
                              ...securitySettings,
                              recording: !securitySettings.recording,
                            })
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.recording
                              ? "translate-x-6"
                              : "translate-x-1"
                              } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                          />
                        </button>
                      </div> */}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div
              className={`flex gap-4 p-6 pt-4 pb-6 border-t ${darkMode ? "border-white/10" : "border-gray-200"
                }`}
            >
              <button
                type="button"
                onClick={() => setShowCreateMeeting(false)}
                className={`px-8 py-3.5 rounded-xl transition-all duration-200 text-sm font-medium ${darkMode
                  ? "border border-white/20 text-white hover:bg-white/10"
                  : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateMeeting}
                className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 text-white py-3.5 rounded-xl font-semibold hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                disabled={!createMeetingForm.title.trim()}
              >
                {meetingsLoading ? <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating Meeting...
                </div> : "Create & Join Meeting"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Stats Modal */}
      {showStatsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div
            className={`${darkMode ? "bg-[#232336]" : "bg-white"
              } rounded-2xl shadow-2xl p-8 md:p-12 w-[95vw] max-w-lg mx-auto relative max-h-[90vh] overflow-y-auto scrollbar-hide border ${darkMode ? "border-white/10" : "border-gray-200"
              }`}
          >
            <button
              className={`absolute top-6 right-6 p-2 rounded-lg transition-all duration-200 ${darkMode
                ? "text-white/60 hover:text-white hover:bg-white/10"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              onClick={() => setShowStatsModal(false)}
            >
              <X size={28} />
            </button>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-purple-600">
                  {selectedStatType === "scheduled" && (
                    <CalendarIcon size={20} className="text-white" />
                  )}
                  {selectedStatType === "attended" && (
                    <CheckCircle size={20} className="text-white" />
                  )}
                  {selectedStatType === "cancelled" && (
                    <XCircle size={20} className="text-white" />
                  )}
                </div>
                <h2
                  className={`text-3xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  {selectedStatType === "scheduled" && "Scheduled Meetings"}
                  {selectedStatType === "attended" && "Attended Meetings"}
                  {selectedStatType === "cancelled" && "Cancelled Meetings"}
                </h2>
              </div>
              <p
                className={`text-lg ${darkMode ? "text-white/60" : "text-gray-600"
                  }`}
              >
                {selectedStatType === "scheduled" &&
                  "View all your upcoming and scheduled meetings"}
                {selectedStatType === "attended" &&
                  "Review all meetings you have successfully attended"}
                {selectedStatType === "cancelled" &&
                  "Check meetings that were cancelled or missed"}
              </p>
            </div>

            {/* Meeting List */}
            <div className="space-y-4">
              {selectedStatType === "scheduled" && (
                <>
                  {meetings
                    .filter((m) => !m.createdByMe)
                    .map((meeting, index) => (
                      <div
                        key={index}
                        className={`group rounded-xl p-6 border transition-all duration-200 cursor-pointer ${darkMode
                          ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                          : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
                          }`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div
                            className={`font-bold text-lg ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            {meeting.title}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1 bg-blue-600/20 text-blue-600 text-xs rounded-full font-medium">
                              Scheduled
                            </span>
                            <span
                              className={`text-sm ${darkMode ? "text-white/60" : "text-gray-500"
                                }`}
                            >
                              {meeting.time}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div
                              className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/70" : "text-gray-600"
                                }`}
                            >
                              <User
                                size={16}
                                className={`${darkMode ? "text-white" : "text-gray-600"
                                  }`}
                              />
                              <span>Hosted by John Smith</span>
                            </div>
                            <div
                              className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/60" : "text-gray-500"
                                }`}
                            >
                              <Hash
                                size={16}
                                className={`${darkMode ? "text-white" : "text-gray-600"
                                  }`}
                              />
                              <span>Room 1432</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${darkMode
                                ? "bg-white/10 text-white hover:bg-white/20"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatsMeetingDetails(meeting);
                              }}
                            >
                              Details
                            </button>
                            <button
                              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${darkMode
                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                                }`}
                            >
                              Join
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                </>
              )}

              {selectedStatType === "attended" && (
                <>
                  {meetings
                    .filter((m) => m.createdByMe)
                    .map((meeting, index) => (
                      <div
                        key={index}
                        className={`group rounded-xl p-6 border transition-all duration-200 cursor-pointer ${darkMode
                          ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                          : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
                          }`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div
                            className={`font-bold text-lg ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            {meeting.title}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1 bg-green-600/20 text-green-600 text-xs rounded-full font-medium">
                              Completed
                            </span>
                            <span
                              className={`text-sm ${darkMode ? "text-white/60" : "text-gray-500"
                                }`}
                            >
                              {meeting.time}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div
                              className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/70" : "text-gray-600"
                                }`}
                            >
                              <User
                                size={16}
                                className={`${darkMode ? "text-white" : "text-gray-600"
                                  }`}
                              />
                              <span>You hosted</span>
                            </div>
                            <div
                              className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/60" : "text-gray-500"
                                }`}
                            >
                              <Hash
                                size={16}
                                className={`${darkMode ? "text-white" : "text-gray-600"
                                  }`}
                              />
                              <span>Room 1432</span>
                            </div>
                          </div>
                          <button
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${darkMode
                              ? "bg-white/10 text-white hover:bg-white/20"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                </>
              )}

              {selectedStatType === "cancelled" && (
                <>
                  {meetings.slice(0, 2).map((meeting, index) => (
                    <div
                      key={index}
                      className={`group rounded-xl p-6 border transition-all duration-200 cursor-pointer ${darkMode
                        ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                        : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
                        }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div
                          className={`font-bold text-lg ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          {meeting.title}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-red-600/20 text-red-600 text-xs rounded-full font-medium">
                            Cancelled
                          </span>
                          <span
                            className={`text-sm ${darkMode ? "text-white/60" : "text-gray-500"
                              }`}
                          >
                            {meeting.time}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/70" : "text-gray-600"
                              }`}
                          >
                            <User
                              size={16}
                              className={`${darkMode ? "text-white" : "text-gray-600"
                                }`}
                            />
                            <span>Hosted by John Smith</span>
                          </div>
                          <div
                            className={`flex items-center gap-2 text-sm ${darkMode ? "text-white/60" : "text-gray-500"
                              }`}
                          >
                            <Hash
                              size={16}
                              className={`${darkMode ? "text-white" : "text-gray-600"
                                }`}
                            />
                            <span>Room 1432</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${darkMode
                              ? "bg-white/10 text-white hover:bg-white/20"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatsMeetingDetails(meeting);
                            }}
                          >
                            Details
                          </button>
                          <button
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${darkMode
                              ? "bg-orange-600 text-white hover:bg-orange-700"
                              : "bg-orange-600 text-white hover:bg-orange-700"
                              }`}
                          >
                            Reschedule
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Empty State */}
              {((selectedStatType === "scheduled" &&
                meetings.filter((m) => !m.createdByMe).length === 0) ||
                (selectedStatType === "attended" &&
                  meetings.filter((m) => m.createdByMe).length === 0) ||
                (selectedStatType === "cancelled" &&
                  meetings.length === 0)) && (
                  <div className="text-center py-16">
                    <div
                      className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full ${darkMode ? "bg-white/5" : "bg-gray-100"
                        }`}
                    >
                      {selectedStatType === "scheduled" && (
                        <CalendarIcon
                          size={32}
                          className={`${darkMode ? "text-white/40" : "text-gray-400"
                            }`}
                        />
                      )}
                      {selectedStatType === "attended" && (
                        <CheckCircle
                          size={32}
                          className={`${darkMode ? "text-white/40" : "text-gray-400"
                            }`}
                        />
                      )}
                      {selectedStatType === "cancelled" && (
                        <XCircle
                          size={32}
                          className={`${darkMode ? "text-white/40" : "text-gray-400"
                            }`}
                        />
                      )}
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      No {selectedStatType} meetings
                    </h3>
                    <p
                      className={`${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      {selectedStatType === "scheduled" &&
                        "You have no upcoming meetings scheduled."}
                      {selectedStatType === "attended" &&
                        "You haven't attended any meetings yet."}
                      {selectedStatType === "cancelled" &&
                        "No meetings have been cancelled."}
                    </p>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
      {/* Notifications Modal */}
      {showNotificationsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="  bg-[#232336] 
  rounded-2xl 
  shadow-2xl 
  p-8 md:p-12 
  w-[95vw] 
  max-w-lg 
  relative 
  max-h-[90vh] 
  overflow-y-auto 
  scrollbar-hide 
  mx-auto">
            <button
              className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors duration-200"
              onClick={() => setShowNotificationsModal(false)}
            >
              <X size={28} />
            </button>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-purple-600">
                  <Bell size={20} className="text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white">Notifications</h2>
              </div>
              <p className="text-white/60 text-lg">
                Manage your notifications and stay updated
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-1 mb-8 bg-white/5 rounded-lg p-1 w-fit">
              <button
                className={`px-6 py-3 rounded-md text-sm font-medium transition ${notificationsTab === "current"
                  ? "bg-white/10 text-white"
                  : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                onClick={() => setNotificationsTab("current")}
              >
                Current ({notifications.length})
              </button>
              <button
                className={`px-6 py-3 rounded-md text-sm font-medium transition ${notificationsTab === "cleared"
                  ? "bg-white/10 text-white"
                  : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                onClick={() => setNotificationsTab("cleared")}
              >
                Cleared ({clearedNotifications.length})
              </button>
            </div>

            {notificationsTab === "current" && (
              <div className="space-y-6">
                {notifications.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-white/5">
                      <Bell size={32} className="text-white/40" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      No notifications
                    </h3>
                    <p className="text-white/60">
                      You're all caught up! Check back later for updates.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4">
                      {notifications.map((n) => (
                        <div
                          key={n.id}
                          className="group bg-white/5 rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all duration-200"
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 mt-1">{n.icon}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white/90 text-base leading-relaxed mb-2">
                                {n.text}
                              </p>
                              <div className="flex items-center gap-4 text-white/40 text-sm">
                                <span>Just now</span>
                                <span>•</span>
                                <span>Unread</span>
                              </div>
                            </div>
                            <button
                              className="flex-shrink-0 p-2 rounded-lg hover:bg-red-600/20 text-white/60 hover:text-red-400 transition-all duration-200 group-hover:opacity-100 opacity-0"
                              onClick={() => handleClearNotification(n.id)}
                              aria-label="Clear notification"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-4 pt-6 border-t border-white/10">
                      <button
                        className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl transition-all duration-200"
                        onClick={() => setShowNotificationsModal(false)}
                      >
                        Close
                      </button>
                      <button
                        className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleClearAll}
                        disabled={notifications.length === 0}
                      >
                        Clear All Notifications
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {notificationsTab === "cleared" && (
              <div className="space-y-6">
                {clearedNotifications.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-white/5">
                      <Trash2 size={32} className="text-white/40" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      No cleared notifications
                    </h3>
                    <p className="text-white/60">
                      Notifications you clear will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {clearedNotifications.map((n) => (
                      <div
                        key={n.id}
                        className="bg-white/5 rounded-xl p-6 border border-white/10 opacity-60"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 mt-1">{n.icon}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white/70 text-base leading-relaxed mb-2 line-through">
                              {n.text}
                            </p>
                            <div className="flex items-center gap-4 text-white/30 text-sm">
                              <span>Cleared</span>
                              <span>•</span>
                              <span>Just now</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Meeting Details Modal */}
      {showMeetingDetails && selectedMeeting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm overflow-hidden"
          onClick={() => setShowMeetingOptionsDropdown(false)}
        >
          <div
            className={`rounded-3xl shadow-2xl border w-[95vw] max-w-3xl relative max-h-[100vh] overflow-hidden ${darkMode
              ? "bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10"
              : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
              }`}
          >
            {/* Back Button - Top Left */}
            {cameFromStatsModal && (
              <button
                className={`absolute top-4 left-4 z-10 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                  ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                  }`}
                onClick={handleBackToStatsModal}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
            )}

            {/* Top Bar with Tabs and Buttons */}
            <div className="relative px-8 pt-10 pb-6">
              <div
                className={`flex items-center ${isEditing ? "justify-between" : "justify-between"
                  }`}
              >
                {/* Left side - Title or Tabs */}
                <div className="flex-1">
                  {isEditing ? (
                    /* Tabs - Only show when editing */
                    <div className="max-w-xl">
                      <div
                        className={`flex gap-1 rounded-xl p-1 ${darkMode ? "bg-white/5" : "bg-gray-100"
                          }`}
                      >
                        <button
                          type="button"
                          onClick={() => setMeetingDetailsTab("details")}
                          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${meetingDetailsTab === "details"
                            ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                            : darkMode
                              ? "text-white/60 hover:text-white hover:bg-white/5"
                              : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                            }`}
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          onClick={() => setMeetingDetailsTab("security")}
                          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${meetingDetailsTab === "security"
                            ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                            : darkMode
                              ? "text-white/60 hover:text-white hover:bg-white/5"
                              : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                            }`}
                        >
                          Security
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Meeting Title and Time - Show when not editing */
                    <div className="text-left">
                      <h2
                        className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        {selectedMeeting.title || "Untitled Meeting"}
                      </h2>
                      <div
                        className={`flex items-center gap-2 mt-1 text-base ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        <CalendarIcon size={18} />
                        {selectedMeeting.meeting_date !== null ? (
                          <span>
                            {formatDate(selectedMeeting.meeting_date)} - {formatTime(selectedMeeting.meeting_date.split('T')[0] + 'T' + selectedMeeting.start_time)} - {formatTime(selectedMeeting.meeting_date.split('T')[0] + 'T' + selectedMeeting.end_time)}
                          </span>
                        ) : <></>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Side Buttons - Always visible */}
                <div className="flex items-center gap-2">
                  {/* More Options Menu - Only show for meetings created by user */}
                  {contextMeetings.some((meeting) => meeting.createdById === user?.id) && (
                    <div
                      className="relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!selectedMeeting.canceled_meeting && (
                        <button
                          className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                            ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                            }`}
                          aria-label="More options"
                          onClick={() =>
                            setShowMeetingOptionsDropdown(
                              !showMeetingOptionsDropdown
                            )
                          }
                        >
                          <MoreVertical size={20} />
                        </button>
                      )}

                      {showMeetingOptionsDropdown && (
                        <div
                          className={`absolute right-0 top-full -mt-0.5 w-48 rounded-xl shadow-2xl border p-2 z-50 ${darkMode
                            ? "bg-[#232336] border-white/10"
                            : "bg-white border-gray-200"
                            }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              handleEditClick();
                              setShowMeetingOptionsDropdown(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${darkMode
                              ? "text-white/80 hover:bg-white/10 hover:text-white"
                              : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                              }`}
                          >
                            <CalendarIcon size={16} />
                            Edit Meeting
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowCancelMeetingModal(true);
                              setShowMeetingOptionsDropdown(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${darkMode
                              ? "text-red-400 hover:bg-red-600/20 hover:text-red-300"
                              : "text-red-600 hover:bg-red-50 hover:text-red-700"
                              }`}
                          >
                            <AlertCircle size={16} />
                            Cancel Meeting
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Close Button */}
                  <button
                    className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                      ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                      }`}
                    aria-label="Close"
                    onClick={() => setShowMeetingDetails(false)}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="px-8 pb-6 overflow-y-auto max-h-[50vh] scrollbar-hide">
              {(meetingDetailsTab === "details" || !isEditing) && (
                <div className="space-y-4">
                  {isEditing && (
                    <>
                      <div>
                        <div className="mb-2">
                          <span
                            className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                              }`}
                          >
                            Title
                          </span>
                        </div>
                        <div
                          className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                            ? "bg-white/5 border border-white/10"
                            : "bg-gray-50 border border-gray-200"
                            }`}
                        >
                          <CalendarIcon
                            size={16}
                            className={`${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          />
                          <input
                            type="text"
                            value={editForm.title}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                title: e.target.value,
                              })
                            }
                            className={`flex-1 text-lg font-medium bg-transparent border-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                              ? "text-white placeholder-white/40"
                              : "text-gray-900 placeholder-gray-500"
                              }`}
                            placeholder="Meeting Title"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="mb-2">
                          <span
                            className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                              }`}
                          >
                            Date
                          </span>
                        </div>
                        <div
                          className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                            ? "bg-white/5 border border-white/10"
                            : "bg-gray-50 border border-gray-200"
                            }`}
                        >
                          <CalendarIcon
                            size={16}
                            className={`${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          />
                          <div className="flex-1">
                            {isEditing ? (
                              <DatePicker
                                selected={editForm.date}
                                onChange={(date) =>
                                  setEditForm({
                                    ...editForm,
                                    date: date || new Date(),
                                  })
                                }
                                dateFormat="MMMM d, yyyy"
                                className={`w-full text-sm font-medium bg-transparent border-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 cursor-pointer ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                                placeholderText="Select date"
                                calendarClassName={`custom-datepicker-calendar ${darkMode ? "dark" : ""
                                  }`}
                                popperClassName="custom-datepicker-popper"
                                wrapperClassName="w-full"
                              />
                            ) : (
                              <div
                                className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                              >
                                {editForm.date.toLocaleDateString("en-US", {
                                  month: "long",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="mb-2">
                            <span
                              className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              Start Time
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                              ? "bg-white/5 border border-white/10"
                              : "bg-gray-50 border border-gray-200"
                              }`}
                          >
                            <Clock
                              size={16}
                              className={`${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                            />
                            <div className="flex-1">
                              {isEditing ? (
                                <select
                                  value={editForm.startTime}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      startTime: e.target.value,
                                    })
                                  }
                                  className={`w-full text-sm font-medium bg-transparent border-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 appearance-none cursor-pointer ${darkMode ? "text-white" : "text-gray-900"
                                    }`}
                                  style={{
                                    backgroundImage: darkMode
                                      ? "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e\")"
                                      : "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e\")",
                                  }}
                                >
                                  {timeOptions.map((time) => (
                                    <option key={time} value={time}>
                                      {time}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <div
                                  className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                    }`}
                                >
                                  {editForm.startTime}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="mb-2">
                            <span
                              className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              End Time
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                              ? "bg-white/5 border border-white/10"
                              : "bg-gray-50 border border-gray-200"
                              }`}
                          >
                            <Clock
                              size={16}
                              className={`${darkMode ? "text-white/60" : "text-gray-600"
                                }`}
                            />
                            <div className="flex-1">
                              {isEditing ? (
                                <select
                                  value={editForm.endTime}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      endTime: e.target.value,
                                    })
                                  }
                                  className={`w-full text-sm font-medium bg-transparent border-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 appearance-none cursor-pointer ${darkMode ? "text-white" : "text-gray-900"
                                    }`}
                                  style={{
                                    backgroundImage: darkMode
                                      ? "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e\")"
                                      : "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e\")",
                                  }}
                                >
                                  {timeOptions.map((time) => (
                                    <option key={time} value={time}>
                                      {time}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <div
                                  className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                    }`}
                                >
                                  {editForm.endTime}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Meeting Info Grid */}
                  {!isEditing && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="mb-2">
                          <span
                            className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                              }`}
                          >
                            Host
                          </span>
                        </div>
                        <div
                          className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                            ? "bg-white/5 border border-white/10"
                            : "bg-gray-50 border border-gray-200"
                            }`}
                        >
                          <User
                            size={16}
                            className={`${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          />
                          <div className="flex-1">
                            <div
                              className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {selectedMeeting?.created_by?.first_name + " " + selectedMeeting?.created_by?.last_name || "John Smith"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-2">
                          <span
                            className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                              }`}
                          >
                            Room
                          </span>
                        </div>
                        <div
                          className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                            ? "bg-white/5 border border-white/10"
                            : "bg-gray-50 border border-gray-200"
                            }`}
                        >
                          <Hash
                            size={16}
                            className={`${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          />
                          <div className="flex-1">
                            <div
                              className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {selectedMeeting?.meetingId}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-2">
                          <span
                            className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                              }`}
                          >
                            Status
                          </span>
                        </div>
                        <div
                          className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                            ? "bg-white/5 border border-white/10"
                            : "bg-gray-50 border border-gray-200"
                            }`}
                        >
                          <CalendarCheck
                            size={16}
                            className={`${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          />
                          <div className="flex-1">
                            <div
                              className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {selectedMeeting.meeting_type || "Scheduled"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-2">
                          <span
                            className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                              }`}
                          >
                            Recurring
                          </span>
                        </div>
                        <div
                          className={`flex items-center gap-2 p-3 rounded-lg ${darkMode
                            ? "bg-white/5 border border-white/10"
                            : "bg-gray-50 border border-gray-200"
                            }`}
                        >
                          <CalendarIcon
                            size={16}
                            className={`${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          />
                          <div className="flex-1">
                            <div
                              className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {/* {selectedMeeting.repeat_status || "None"} {selectedMeeting.repeat_days?.length ? `(${selectedMeeting.repeat_days.join(', ')})` : ''} */}
                              {selectedMeeting.repeat_status || "None"}{" "}
                              {selectedMeeting.repeat_days?.length
                                ? `(${selectedMeeting.repeat_days
                                  .map((day: any) => {
                                    switch (day.toLowerCase()) {
                                      case "monday": return "Mon";
                                      case "tuesday": return "Tue";
                                      case "wednesday": return "Wed";
                                      case "thursday": return "Thu";
                                      case "friday": return "Fri";
                                      case "saturday": return "Sat";
                                      case "sunday": return "Sun";
                                      default: return day;
                                    }
                                  })
                                  .join(", ")})`
                                : ""}


                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recurring Field (Edit Mode Only) */}
                  {isEditing && (
                    <div>
                      <div className="mb-2">
                        <span
                          className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                            }`}
                        >
                          Recurring
                        </span>
                      </div>
                      <div className={`flex flex-col gap-4 sm:ml-auto gap-2 p-3 rounded-lg  ${darkMode
                        ? "bg-white/5 border border-white/10"
                        : "bg-gray-50 border border-gray-200"
                        }`}>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm ${darkMode ? "text-white/70" : "text-gray-700"}`}
                          >
                            Repeat
                          </span>
                          <select
                            value={recurringFrequency}
                            onChange={(e) => setRecurringFrequency(e.target.value as "None" | "Daily" | "Weekly" | "Biweekly" | "Monthly" | "Yearly")}
                            className={`rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 ${darkMode
                              ? "bg-white/10 border border-white/20 text-white"
                              : "bg-white border border-gray-200 text-gray-900"
                              }`}
                          >
                            <option value="None">None</option>
                            <option value="Daily">Daily</option>
                            <option value="Weekly">Weekly</option>
                            <option value="Biweekly">Bi-weekly</option>
                            <option value="Monthly">Monthly</option>
                            <option value="Yearly">Yearly</option>
                          </select>
                        </div>

                        {/* Recurring Date Range */}
                        {recurringFrequency !== "None" && (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label
                                className={`block mb-2 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                  }`}
                              >
                                Start Date
                              </label>
                              <DatePicker
                                selected={recurringStartDate}
                                onChange={setRecurringStartDate}
                                minDate={new Date()}
                                dateFormat="MMM d, yyyy"
                                className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                                  ? "bg-white/10 border border-white/20 text-white"
                                  : "bg-white border border-gray-200 text-gray-900"
                                  }`}
                              />
                            </div>
                            <div>
                              <label
                                className={`block mb-2 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                  }`}
                              >
                                End Date (Optional)
                              </label>
                              <DatePicker
                                selected={recurringEndDate}
                                onChange={setRecurringEndDate}
                                minDate={recurringStartDate || new Date()}
                                dateFormat="MMM d, yyyy"
                                placeholderText="No end date"
                                isClearable
                                className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                                  ? "bg-white/10 border border-white/20 text-white"
                                  : "bg-white border border-gray-200 text-gray-900"
                                  }`}
                              />
                            </div>
                          </div>
                        )}

                        {/* Day selector - hide for Daily */}
                        {recurringFrequency !== "Daily" && (
                          <div className="flex flex-col gap-3">
                            <span
                              className={`text-sm ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              On days:
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { value: "Sunday", label: "Sun" },
                                { value: "Monday", label: "Mon" },
                                { value: "Tuesday", label: "Tue" },
                                { value: "Wednesday", label: "Wed" },
                                { value: "Thursday", label: "Thu" },
                                { value: "Friday", label: "Fri" },
                                { value: "Saturday", label: "Sat" },
                              ].map((day) => (
                                <button
                                  key={day.value}
                                  type="button"
                                  onClick={() => handleDayToggle(day.value)}
                                  className={`w-10 h-10 rounded-lg border-2 transition-all duration-200 flex items-center justify-center text-sm font-medium ${recurringDays.includes(day.value)
                                    ? "bg-gradient-to-r from-pink-600 to-red-500 border-pink-500 text-white shadow-lg"
                                    : darkMode
                                      ? "bg-white/5 border-white/20 text-white/60 hover:bg-white/10 hover:border-white/30 hover:text-white"
                                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                                    }`}
                                >
                                  {day.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <div className="mb-2">
                      <span
                        className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                          }`}
                      >
                        Description
                      </span>
                    </div>
                    {isEditing ? (
                      <div className="relative">
                        <textarea
                          value={editForm.description}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              description: e.target.value,
                            })
                          }
                          className={`w-full rounded-lg px-3 py-2 transition-all duration-200 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 resize-none ${darkMode
                            ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
                            : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                            }`}
                          rows={3}
                          placeholder="Enter meeting description..."
                        />
                      </div>
                    ) : (
                      <div
                        className={`rounded-lg p-3 ${darkMode
                          ? "bg-white/5 border border-white/10"
                          : "bg-gray-50 border border-gray-200"
                          }`}
                      >
                        <p
                          className={`text-sm leading-relaxed ${darkMode ? "text-white/80" : "text-gray-700"
                            }`}
                        >
                          {selectedMeeting.description ||
                            ""}
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"}`}>
                        Participants ({selectedMeeting?.participantDetails?.length || 0})
                      </span>
                      {/* {selectedMeeting?.created_by?.id === user?.id && (
                        <button
                          className={`px-2 py-1 text-xs rounded transition-all duration-200 ${darkMode
                              ? "bg-white/10 text-white hover:bg-white/20"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          onClick={() => setShowInviteModal(true)}
                        >
                          + Invite
                        </button>
                      )} */}
                    </div>
                    {/* Participants Section */}
                    {isEditing && <div className="space-y-4 mb-4">
                      <div className="group participant-input-container">
                        <div className="relative">
                          <input
                            type="text"
                            value={participantInput}
                            onChange={(e) => handleParticipantInputChange(e.target.value)}
                            onKeyPress={handleKeyPress}
                            // onFocus={handleParticipantInputFocus}
                            placeholder="Search by name or email..."
                            className={`w-full rounded-xl px-4 py-3 transition-all duration-200 pr-12 text-base focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                              ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
                              : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                              }`}
                          />
                          <Users
                            className={`absolute right-4 top-1/2 -translate-y-1/2 ${darkMode ? "text-white/40" : "text-gray-400"
                              }`}
                            size={18}
                          />

                          {/* Suggestions Dropdown */}
                          {showParticipantSuggestions && (
                            <div
                              className={`absolute top-full left-0 right-0 mt-2 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto backdrop-blur-sm ${darkMode
                                ? "bg-white/10 border border-white/20"
                                : "bg-white border border-gray-200"
                                }`}
                            >
                              {isLoadingUsers ? (
                                <div className={`p-3 text-center ${darkMode ? "text-white/60" : "text-gray-500"
                                  }`}>
                                  <div className="flex items-center justify-center">
                                    <RefreshCw size={16} className="animate-spin mr-2" />
                                    Searching users...
                                  </div>
                                </div>
                              ) : (
                                filteredSuggestions.length > 0 ? (
                                  filteredSuggestions.map((user) => (
                                    <button
                                      key={user.id}
                                      type="button"
                                      onClick={() => handleAddParticipant(user.email)}
                                      className={`w-full flex items-center gap-3 p-3 transition-colors duration-200 text-left ${darkMode
                                        ? "hover:bg-white/10"
                                        : "hover:bg-gray-50"
                                        }`}
                                    >
                                      {user.avatar ? (
                                        <img
                                          src={user.avatar}
                                          alt={user.name}
                                          className="w-8 h-8 rounded-full border border-white/20"
                                        />
                                      ) : (
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${darkMode ? "bg-white/20 text-white" : "bg-gray-200 text-gray-700"
                                          }`}>
                                          {user.name.charAt(0).toUpperCase()}
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <div className={`font-medium text-sm truncate ${darkMode ? "text-white" : "text-gray-900"
                                          }`}>
                                          {user.name}
                                          {user.isMockUser === false && (
                                            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${darkMode ? "bg-blue-900/50 text-blue-200" : "bg-blue-100 text-blue-800"
                                              }`}>
                                              User
                                            </span>
                                          )}
                                        </div>
                                        <div className={`text-xs truncate ${darkMode ? "text-white/60" : "text-gray-600"
                                          }`}>
                                          {user.email}
                                        </div>
                                      </div>
                                    </button>
                                  ))
                                ) : (
                                  <div className={`p-3 text-center ${darkMode ? "text-white/60" : "text-gray-500"
                                    }`}>
                                    No matching users found
                                    {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participantInput.trim()) && (
                                      <div className="mt-2">
                                        <button
                                          className={`text-sm px-3 py-1.5 rounded-lg ${darkMode
                                            ? "bg-blue-600/30 hover:bg-blue-600/50 text-white"
                                            : "bg-blue-50 hover:bg-blue-100 text-blue-600"
                                            }`}
                                          onClick={() => handleAddParticipant(participantInput.trim())}
                                        >
                                          Add {participantInput.trim()} as external guest
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>

                        {participantDuplicateMsg && (
                          <p className="mt-1 text-xs text-amber-500 font-medium">{participantDuplicateMsg}</p>
                        )}

                        {/* Selected Participants */}
                        {selectedMeeting?.participants?.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {selectedMeeting.participants.map((email: string) => (
                              <div
                                key={email}
                                className={`flex items-center gap-2 p-2 rounded-lg ${darkMode
                                  ? "bg-white/5 border border-white/10"
                                  : "bg-gray-50 border border-gray-200"
                                  }`}
                              >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${darkMode ? "bg-white/10" : "bg-gray-200"
                                  }`}>
                                  {email[0].toUpperCase()}
                                </div>
                                <span className={`flex-1 text-sm truncate ${darkMode ? "text-white" : "text-gray-900"
                                  }`}>
                                  {email}
                                </span>
                                <button
                                  onClick={() => handleRemoveParticipant(email)}
                                  className={`p-1 rounded-lg transition-colors ${darkMode
                                    ? "hover:bg-white/10 text-white/60 hover:text-white"
                                    : "hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>}
                    <div className="space-y-2">
                      {/* Show meeting creator first */}
                      <div className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-white/5 border border-white/10" : "bg-gray-50 border border-gray-200"
                        }`}>
                        <img
                          src={selectedMeeting?.created_by?.profile_picture
                            ? `${process.env.REACT_APP_BACKEND_URL}${selectedMeeting.created_by.profile_picture}`
                            : "https://randomuser.me/api/portraits/men/32.jpg"}
                          alt={selectedMeeting?.created_by?.first_name}
                          className={`w-8 h-8 rounded-full object-cover ${darkMode ? "border border-white/20" : "border border-gray-300"
                            }`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-gray-900"
                              }`}>
                              {`${selectedMeeting?.created_by?.first_name} ${selectedMeeting?.created_by?.last_name}`}
                            </span>
                            <span className="px-1.5 py-0.5 bg-gradient-to-r from-pink-600 to-red-500 text-white text-xs rounded-full font-medium">
                              Host
                            </span>
                          </div>
                          <div className={`text-xs truncate ${darkMode ? "text-white/60" : "text-gray-600"
                            }`}>
                            {selectedMeeting?.created_by?.email}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`px-1.5 py-0.5 text-xs rounded-md flex items-center gap-1 ${darkMode
                            ? "bg-green-600/20 text-green-400 border border-green-400/30"
                            : "bg-green-50 text-green-700 border border-green-200"
                            }`}>
                            <CheckCircle size={10} className="flex-shrink-0" />
                            <span className="hidden sm:inline font-medium">Host</span>
                          </span>
                        </div>
                      </div>

                      {/* Show other participants with real profiles or fallback to dummy */}
                      {selectedMeeting?.participantDetails?.map((participant: any, index: number) => {
                        const avatar = getParticipantAvatar(participant, index);
                        const displayName = getParticipantDisplayName(participant);
                        const hasReal = hasRealProfile(participant);

                        return (
                          <div
                            key={index}
                            className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? "bg-white/5 border border-white/10" : "bg-gray-50 border border-gray-200"
                              }`}
                          >
                            <div className="relative">
                              <img
                                src={avatar}
                                alt={displayName}
                                className={`w-8 h-8 rounded-full object-cover ${darkMode ? "border border-white/20" : "border border-gray-300"
                                  }`}
                              />
                              {hasReal && (
                                <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-400 border border-white rounded-full"></div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium truncate ${darkMode ? "text-white" : "text-gray-900"
                                  }`}>
                                  {displayName}
                                </span>
                                {!hasReal && (
                                  <span className={`text-xs px-1 py-0.5 rounded ${darkMode
                                    ? "bg-yellow-600/20 text-yellow-400 border border-yellow-400/30"
                                    : "bg-yellow-50 text-yellow-700 border border-yellow-200"
                                    }`}>
                                    Invited
                                  </span>
                                )}
                              </div>
                              <div className={`text-xs truncate ${darkMode ? "text-white/60" : "text-gray-600"
                                }`}>
                                {getParticipantEmail(participant)}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {(() => {
                                const rs =
                                  participant.response_status ||
                                  (participant.accepted ? "accepted" : "pending");
                                const statusMap: Record<string, { label: string; Icon: any; dark: string; light: string }> = {
                                  accepted: { label: "Accepted", Icon: CheckCircle, dark: "bg-green-600/20 text-green-400 border-green-400/30", light: "bg-green-50 text-green-700 border-green-200" },
                                  tentative: { label: "Tentative", Icon: Clock, dark: "bg-yellow-600/20 text-yellow-400 border-yellow-400/30", light: "bg-yellow-50 text-yellow-700 border-yellow-200" },
                                  rejected: { label: "Declined", Icon: XCircle, dark: "bg-red-600/20 text-red-400 border-red-400/30", light: "bg-red-50 text-red-700 border-red-200" },
                                  pending: { label: "Pending", Icon: AlertCircle, dark: "bg-blue-600/20 text-blue-400 border-blue-400/30", light: "bg-blue-50 text-blue-700 border-blue-200" },
                                };
                                const s = statusMap[rs] || statusMap.pending;
                                const Icon = s.Icon;
                                return (
                                  <span className={`px-1.5 py-0.5 text-xs rounded-md flex items-center gap-1 border ${darkMode ? s.dark : s.light}`}>
                                    <Icon size={10} className="flex-shrink-0" />
                                    <span className="hidden sm:inline font-medium">{s.label}</span>
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {meetingDetailsTab === "security" && isEditing && (
                <div className="space-y-4">
                  {/* Security Settings */}
                  <div
                    className={`rounded-xl p-4 border ${darkMode
                      ? "bg-white/5 border-white/10"
                      : "bg-gray-50 border-gray-200"
                      }`}
                  >
                    <div className="space-y-4">
                      {/* Password Protection */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4
                            className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            Password Protection
                          </h4>
                          <p
                            className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          >
                            Require a password to join the meeting
                          </p>
                        </div>
                        <button
                          type="button"
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.requirePassword
                            ? "bg-gradient-to-r from-pink-500 to-red-500"
                            : darkMode
                              ? "bg-white/20"
                              : "bg-gray-300"
                            }`}
                          onClick={() =>
                            setSecuritySettings({
                              ...securitySettings,
                              requirePassword: !securitySettings.requirePassword,
                            })
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.requirePassword
                              ? "translate-x-6"
                              : "translate-x-1"
                              } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                          />
                        </button>
                      </div>

                      {securitySettings.requirePassword && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <label
                              className={`text-sm font-medium whitespace-nowrap ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              Meeting Password
                            </label>
                            <div className="w-64 flex relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter Your Password"
                                className={`w-full rounded-lg px-3 py-2 pr-20 transition-all duration-200 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                                  ? "bg-white/10 border border-white/20 text-white placeholder-white/40"
                                  : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                                  }`}
                                value={securitySettings.password}
                                onChange={(e) =>
                                  setSecuritySettings({
                                    ...securitySettings,
                                    password: e.target.value,
                                  })
                                }
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${darkMode
                                  ? "text-white/60 hover:text-white"
                                  : "text-gray-400 hover:text-gray-600"
                                  }`}
                              >
                                {showPassword ? (
                                  <Eye size={16} />
                                ) : (
                                  <EyeOff size={16} />
                                )}
                              </button>
                            </div>
                            <CopyButton
                              text={securitySettings.password}
                              darkMode={darkMode}
                            />
                          </div>
                        </div>
                      )}

                      {/* Waiting Room */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4
                            className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            Waiting Room
                          </h4>
                          <p
                            className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          >
                            Participants must be admitted by the host
                          </p>
                        </div>
                        <button
                          type="button"
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.waitingRoom
                            ? "bg-gradient-to-r from-pink-500 to-red-500"
                            : darkMode
                              ? "bg-white/20"
                              : "bg-gray-300"
                            }`}
                          onClick={() =>
                            setSecuritySettings({
                              ...securitySettings,
                              waitingRoom: !securitySettings.waitingRoom,
                            })
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.waitingRoom
                              ? "translate-x-6"
                              : "translate-x-1"
                              } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                          />
                        </button>
                      </div>

                      {/* Mute on Entry */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4
                            className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                              }`}
                          >
                            Mute on Entry
                          </h4>
                          <p
                            className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                          >
                            Automatically mute participants when they join
                          </p>
                        </div>
                        <button
                          type="button"
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.muteOnEntry
                            ? "bg-gradient-to-r from-pink-500 to-red-500"
                            : darkMode
                              ? "bg-white/20"
                              : "bg-gray-300"
                            }`}
                          onClick={() =>
                            setSecuritySettings({
                              ...securitySettings,
                              muteOnEntry: !securitySettings.muteOnEntry,
                            })
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.muteOnEntry
                              ? "translate-x-6"
                              : "translate-x-1"
                              } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                          />
                        </button>
                      </div>

                      {/* Allow Recording */}
                      {/* <div className="flex items-center justify-between">
          <div>
            <h4
              className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                }`}
            >
              Allow Recording
            </h4>
            <p
              className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                }`}
            >
              Participants can record the meeting
            </p>
          </div>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.recording
              ? "bg-gradient-to-r from-pink-500 to-red-500"
              : darkMode
                ? "bg-white/20"
                : "bg-gray-300"
              }`}
            onClick={() =>
              setSecuritySettings({
                ...securitySettings,
                recording: !securitySettings.recording,
              })
            }
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.recording
                ? "translate-x-6"
                : "translate-x-1"
                } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
            />
          </button>
        </div> */}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            {/* Actions */}
            <div
              className={`flex gap-4 p-6 pt-4 pb-6 border-t ${darkMode ? "border-white/10" : "border-gray-200"
                }`}
            >
              {isEditing ? (
                <>
                  {/* Edit Mode Buttons */}
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className={`px-8 py-3.5 rounded-xl transition-all duration-200 text-sm font-medium ${darkMode
                      ? "border border-white/20 text-white hover:bg-white/10"
                      : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 text-white py-3.5 rounded-xl font-semibold hover:scale-105 transition-all duration-200 shadow-lg text-sm"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                // View Mode Buttons (The updated logic is here)
                <>
                  {(selectedMeeting.canceled_meeting || selectedMeeting.completed_status) ? (
                    // If CANCELLED or COMPLETED, show CLOSE button
                    <button
                      type="button"
                      onClick={() => setShowMeetingDetails(false)}
                      className={`flex-1 px-8 py-3.5 rounded-xl transition-all duration-200 text-sm font-medium ${darkMode
                        ? "border border-white/20 text-white hover:bg-white/10"
                        : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      Close Details
                    </button>
                  ) : (
                    // If SCHEDULED/ACTIVE, show JOIN button
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMeetingDetails(false);
                        // navigate(`/meeting-new`); // Removed as requested in previous interaction
                        // window.open(`${selectedMeeting.meeting_link}`, "_blank"); // Removed as requested in previous interaction
                        joinMeetingNew(selectedMeeting.id);
                      }}
                      className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 text-white py-3.5 rounded-xl font-semibold hover:scale-105 transition-all duration-200 shadow-lg text-sm"
                    >
                      Join Meeting
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Invite Participants Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm overflow-hidden">
          <div
            className={`rounded-3xl shadow-2xl border w-[95vw] max-w-2xl relative max-h-[100vh] overflow-hidden ${darkMode
              ? "bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10"
              : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
              }`}
          >
            {/* Close Button */}
            <button
              className={`absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                }`}
              aria-label="Close"
              onClick={() => setShowInviteModal(false)}
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div className="relative px-8 pt-8 pb-6">
              <div className="text-left">
                <h2
                  className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Invite Participants
                </h2>
              </div>
            </div>

            {/* Content Area */}
            <div className="px-8 pb-6 overflow-y-auto max-h-[45vh] scrollbar-hide">
              <div className="space-y-8">
                {/* Participants Field */}
                <div className="group participant-input-container">
                  <label
                    className={`block mb-4 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                      }`}
                  >
                    Participants
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={inviteEmail}
                      onChange={(e) =>
                        handleParticipantInputChange(e.target.value)
                      }
                      onKeyPress={handleKeyPress}
                      // onFocus={handleParticipantInputFocus}
                      placeholder="Search by name or email..."
                      className={`w-full rounded-xl px-4 py-3 transition-all duration-200 pr-12 text-base  focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                        ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
                        : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                        }`}
                    />
                    <Users
                      className={`absolute right-4 top-1/2 -translate-y-1/2 ${darkMode ? "text-white/40" : "text-gray-400"
                        }`}
                      size={18}
                    />

                    {/* Autocomplete Suggestions */}
                    {showParticipantSuggestions &&
                      filteredSuggestions.length > 0 && (
                        <div
                          className={`absolute top-full left-0 right-0 mt-2 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto backdrop-blur-sm ${darkMode
                            ? "bg-white/10 border border-white/20"
                            : "bg-white border border-gray-200"
                            }`}
                        >
                          {filteredSuggestions.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => handleAddParticipant(user.email)}
                              className={`w-full flex items-center gap-3 p-3 transition-colors duration-200 text-left ${darkMode
                                ? "hover:bg-white/10"
                                : "hover:bg-gray-50"
                                }`}
                            >
                              <img
                                src={user.avatar}
                                alt={user.name}
                                className="w-8 h-8 rounded-full border border-white/20"
                              />
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`font-medium text-sm truncate ${darkMode ? "text-white" : "text-gray-900"
                                    }`}
                                >
                                  {user.name}
                                </div>
                                <div
                                  className={`text-xs truncate ${darkMode ? "text-white/60" : "text-gray-600"
                                    }`}
                                >
                                  {user.email}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                  </div>

                  <p
                    className={`text-xs mt-2 ${darkMode ? "text-white/40" : "text-gray-500"
                      }`}
                  >
                    Type to search or press Enter to add
                  </p>

                  {/* Empty State */}
                  {inviteList.length === 0 && (
                    <div className="mt-8 text-center">
                      <div
                        className={`w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full ${darkMode ? "bg-white/5" : "bg-gray-100"
                          }`}
                      >
                        <Users
                          size={24}
                          className={`${darkMode ? "text-white/40" : "text-gray-400"
                            }`}
                        />
                      </div>
                      <p
                        className={`text-sm ${darkMode ? "text-white/60" : "text-gray-500"
                          }`}
                      >
                        No participants selected yet
                      </p>
                      <p
                        className={`text-xs mt-1 ${darkMode ? "text-white/40" : "text-gray-400"
                          }`}
                      >
                        Start typing to search and add participants
                      </p>
                    </div>
                  )}

                  {/* Selected Participants */}
                  {inviteList.length > 0 && (
                    <div className="mt-6 space-y-3">
                      <div
                        className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                          }`}
                      >
                        Selected participants:
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {inviteList.map((email, index) => {
                          const user = mockUsers.find((u) => u.email === email);
                          return (
                            <div
                              key={index}
                              className={`flex items-center gap-3 rounded-lg px-4 py-3 ${darkMode
                                ? "bg-white/10 border border-white/20"
                                : "bg-gray-100 border border-gray-200"
                                }`}
                            >
                              {user && (
                                <img
                                  src={user.avatar}
                                  alt={user.name}
                                  className="w-6 h-6 rounded-full border border-white/20"
                                />
                              )}
                              <span
                                className={`text-sm ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                              >
                                {user ? user.name : email}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveInvite(email)}
                                className={`transition-colors duration-200 ${darkMode
                                  ? "text-white/40 hover:text-white/80"
                                  : "text-gray-400 hover:text-gray-600"
                                  }`}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div
              className={`flex gap-4 p-8 pt-6 pb-8 border-t ${darkMode ? "border-white/10" : "border-gray-200"
                }`}
            >
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className={`px-8 py-3.5 rounded-xl transition-all duration-200 text-sm font-medium ${darkMode
                  ? "border border-white/20 text-white hover:bg-white/10"
                  : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendInvites}
                className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 text-white py-3.5 rounded-xl font-semibold hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                disabled={inviteList.length === 0}
              >
                Add Participants ({inviteList.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm overflow-hidden">
          <div
            className={`rounded-3xl shadow-2xl border w-[95vw] max-w-md relative ${darkMode
              ? "bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10"
              : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
              }`}
          >
            {/* Close Button */}
            <button
              className={`absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                }`}
              aria-label="Close"
              onClick={closePasswordModal}
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div className="relative px-8 pt-8 pb-6">
              <div className="text-center">
                {/* Icon */}
                <div
                  className={`w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full ${darkMode ? "bg-pink-600/20" : "bg-pink-100"
                    }`}
                >
                  <Lock size={24} className="text-pink-500" />
                </div>

                <h2
                  className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Enter Meeting Password
                </h2>

                <p
                  className={`text-sm mt-2 ${darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                >
                  This meeting is protected. Please enter the password to continue.
                </p>

                {attempts > 0 && (
                  <p className="text-sm mt-2 text-white/60">
                    Attempts: {attempts} / 5
                  </p>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="px-8 pb-4">
              <input
                type="password"
                disabled={lockout}
                className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 text-sm ${lockout
                  ? "bg-gray-300/40 text-gray-400 cursor-not-allowed"
                  :
                  darkMode
                    ? "bg-white/5 border-white/20 text-white placeholder-white/40 focus:border-pink-400"
                    : "bg-gray-50 border-gray-300 text-gray-800 placeholder-gray-400 focus:border-pink-500"
                  }`}
                // placeholder="Enter meeting password"
                placeholder={lockout ? `Locked for ${lockoutTimer}s` : "Enter password"}
                value={enteredPassword}
                onChange={(e) => setEnteredPassword(e.target.value)}
              />

              {passwordError && (
                <p className="mt-2 text-sm text-red-500">{passwordError}</p>
              )}
            </div>

            {/* Actions */}
            <div
              className={`flex gap-4 p-8 pt-4 pb-8 border-t ${darkMode ? "border-white/10" : "border-gray-200"
                }`}
            >
              <button
                type="button"
                onClick={closePasswordModal}
                className={`px-8 py-3.5 rounded-xl transition-all duration-200 text-sm font-medium ${darkMode
                  ? "border border-white/20 text-white hover:bg-white/10"
                  : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Cancel
              </button>

              <button
                type="button"
                // onClick={handleSubmitPassword}
                onClick={!lockout ? handleSubmitPassword : undefined}
                disabled={lockout}
                className={`flex-1 py-3.5 rounded-xl font-semibold transition-all duration-200 shadow-lg text-sm
    ${lockout
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-pink-500 to-red-500 hover:scale-105 text-white"
                  }`}
              >
                {lockout ? `Wait ${lockoutTimer}s` : "Join Meeting"}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Cancel Meeting Confirmation Modal */}
      {showCancelMeetingModal && selectedMeeting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm overflow-hidden">
          <div
            className={`rounded-3xl shadow-2xl border w-[95vw] max-w-md relative ${darkMode
              ? "bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10"
              : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
              }`}
          >
            {/* Close Button */}
            <button
              className={`absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
                ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
                }`}
              aria-label="Close"
              onClick={() => setShowCancelMeetingModal(false)}
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div className="relative px-8 pt-8 pb-6">
              <div className="text-center">
                <div
                  className={`w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full ${darkMode ? "bg-red-600/20" : "bg-red-100"
                    }`}
                >
                  <AlertCircle size={24} className="text-red-500" />
                </div>
                <h2
                  className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Cancel Meeting
                </h2>
                <p
                  className={`text-sm mt-2 ${darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                >
                  Are you sure you want to cancel "{selectedMeeting.title}"?
                  This action cannot be undone.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div
              className={`flex gap-4 p-8 pt-4 pb-8 border-t ${darkMode ? "border-white/10" : "border-gray-200"
                }`}
            >
              <button
                type="button"
                onClick={() => setShowCancelMeetingModal(false)}
                className={`px-8 py-3.5 rounded-xl transition-all duration-200 text-sm font-medium ${darkMode
                  ? "border border-white/20 text-white hover:bg-white/10"
                  : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Keep Meeting
              </button>
              <button
                type="button"
                onClick={handleCancelMeeting}
                className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white py-3.5 rounded-xl font-semibold hover:scale-105 transition-all duration-200 shadow-lg text-sm"
              >
                Cancel Meeting
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
